import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import FinixTokenizationForm from "../../components/FinixTokenizationForm";
import { FINIX_TEST_CARDS } from "../../lib/finixFlex";
import { parseResponseSafely } from "../../lib/utils";
import SystemRecoveryPanel from "./SystemRecoveryPanel";
import {
  Envelope,
  CheckCircle,
  Warning,
  Eye,
  EyeSlash,
  PaperPlaneTilt,
  ChartLine,
  Gear,
  ArrowClockwise,
  CreditCard,
  ChatCircleText,
  Phone,
  Translate,
  Megaphone,
  Copy,
  Trash,
  Plus,
  Info,
  ListBullets,
  ArrowCounterClockwise,
  X,
  Play,
  Lightning,
  User,
  Wallet,
} from "phosphor-react";

// Feature flags for tab visibility
const ENABLE_ADVERTISING_ADMIN = process.env.REACT_APP_ENABLE_ADVERTISING_ADMIN === "1";
const ENABLE_WEBHOOKS_ADMIN = process.env.REACT_APP_ENABLE_WEBHOOKS_ADMIN === "1";
const ENABLE_ADMIN_JOB_RUNNER = process.env.REACT_APP_ENABLE_ADMIN_JOB_RUNNER === "1";
const ENABLE_CUSTOM_ADS_ADMIN = process.env.REACT_APP_ENABLE_CUSTOM_ADS_ADMIN !== "0"; // Enabled by default
const ENABLE_TRUSTCOMMERCE_TAB = false; // DEPRECATED: TrustCommerce tab hidden (migration to Finix complete)
import { ServiceTypesPanel } from "./AdminServiceTypes";
import { CustomAdsPanel } from "./CustomAdsPanel";
import AdminBillingOps from "./AdminBillingOps";
import { CommsLedgerPanel } from "./CommsLedgerPanel";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminSystemUtilities() {
  const [searchParams, setSearchParams] = useSearchParams();
  // URL compatibility: "logs" was old Comms tab, now redirect to "comms-ledger"
  const rawTab = searchParams.get("tab") || "comms-ledger";
  const initialTab = rawTab === "logs" ? "comms-ledger" : rawTab;
  const [activeTab, setActiveTab] = useState(initialTab);

  // DEV MODE: Tab registry invariant check - prevent regressions where TabsTrigger lacks matching TabsContent
  // All tab values that exist in this component (some may be gated by feature flags)
  const ALL_TAB_VALUES = [
    "comms-ledger",   // Comms - Operations (was "logs", now consolidated)
    "payment-logs",   // Payment Logs - Operations
    "jobs",           // Jobs - Operations (gated)
    "billing-ops",    // Billing Ops - NEW (replaces TrustCommerce as primary billing admin)
    "trustcommerce",  // TrustCommerce - DEPRECATED (hidden via ENABLE_TRUSTCOMMERCE_TAB)
    "finix",          // Finix - Integrations (new payment processor)
    "sendgrid",       // SendGrid - Integrations
    "twilio",         // Twilio SMS - Integrations
    "translation",    // Translation - Integrations
    "service-types",  // Service Types - Configuration
    "adsense",        // Advertising - Internal (gated)
    "events",         // Webhooks - Internal (gated)
    "custom_ads",     // Custom Ads - Internal
  ];

  // Update URL when tab changes
  const handleTabChange = (value) => {
    setActiveTab(value);
    if (value === "comms-ledger") {
      setSearchParams({});
    } else {
      setSearchParams({ tab: value });
    }
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showTCPassword, setShowTCPassword] = useState(false);
  const [showTwilioToken, setShowTwilioToken] = useState(false);

  // SendGrid settings
  const [sendgridSettings, setSendgridSettings] = useState({
    api_key: "",
    api_key_masked: "",
    api_key_configured: false,
    from_email: "noreply@freeservicepro.com",
    from_name: "Free Service Pro",
    reply_to_email: "",
    environment: "development",
    send_in_dev: false,
    email_types: {
      welcome: { enabled: true, template_id: "" },
      email_verification: { enabled: true, template_id: "" },
      password_reset: { enabled: true, template_id: "" },
      job_scheduled: { enabled: true, template_id: "" },
    },
  });

  // TrustCommerce settings
  const [tcSettings, setTcSettings] = useState({
    custid: "",
    custid_masked: "",
    custid_configured: false,
    custid_valid: false,
    password: "",
    password_configured: false,
    licensekey: "",
    licensekey_configured: false,
    mode: "test",
    vault_enabled: false,
    avs_enabled: true,
    cvv_enabled: true,
    test_status: null,  // "success" or "failed"
    last_test: null,
  });
  const [showTCLicenseKey, setShowTCLicenseKey] = useState(false);
  const [tcCustIdError, setTcCustIdError] = useState("");

  // Finix settings (new payment processor)
  // Note: Finix uses HTTP Basic Auth with username/password (not API keys)
  // Backend stores as api_key/api_secret but UI shows username/password
  const [finixSettings, setFinixSettings] = useState({
    enabled: false,
    active_environment: "sandbox",  // "sandbox" | "production"
    use_dual_env_resolver: false,   // Kill switch for dual-env credential resolution
    sandbox: {
      merchant_id: "",
      application_id: "",
      username: "",
      password: "",
      username_masked: null,
      username_present: false,
      password_present: false,
    },
    production: {
      merchant_id: "",
      application_id: "",
      username: "",
      password: "",
      username_masked: null,
      username_present: false,
      password_present: false,
    },
    version: 0,
    legacy_detected: false,
    health_preview: null,
    // Legacy flat fields for backwards compatibility
    environment: "sandbox",
    merchant_id: "",
    application_id: "",
    username: "",
    password: "",
    has_username: false,
    has_password: false,
    configured: false,
  });
  const [savingFinix, setSavingFinix] = useState(false);
  
  // Finix tokenization test state (PASS 2)
  const [finixTestToken, setFinixTestToken] = useState(null);
  const [finixTestError, setFinixTestError] = useState(null);
  const [finixTestLoading, setFinixTestLoading] = useState(false);
  const [showTokenizationTest, setShowTokenizationTest] = useState(false);

  // Finix Webhook Settings state
  const [finixWebhookStatus, setFinixWebhookStatus] = useState({
    env_kill_switch_enabled: false,
    db_env_override_enabled: false,
    effective_env_enabled: false,
    app_webhooks_enabled: false,
    webhook_secret_present: false,
    webhook_url: "",
    last_event: null,
  });
  const [finixWebhookSecret, setFinixWebhookSecret] = useState("");
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  // Twilio settings
  const [twilioSettings, setTwilioSettings] = useState({
    account_sid: "",
    account_sid_masked: "",
    account_sid_configured: false,
    auth_token: "",
    auth_token_configured: false,
    phone_number: "",
    mode: "test",
    messaging_service_sid: "",
  });

  // Translation settings
  const [translationSettings, setTranslationSettings] = useState({
    enabled: false,
    provider: "openai",
    model: "gpt-4.1-mini",
    target_language: "es",
    api_key_configured: false,
  });
  const [testText, setTestText] = useState("Hello, this is a test message for translation.");
  const [translationResult, setTranslationResult] = useState(null);

  // AdSense settings
  const [adsenseSettings, setAdsenseSettings] = useState({
    ads_enabled: false,
    ads_mode: "test",
    adsense_publisher_id: "",
    publisher_id_masked: "",
    publisher_id_configured: false,
    ad_units: [],
    frequency_caps: {
      max_impressions_per_session: 5,
      max_impressions_per_day: 20
    },
    domains: [],
    ads_txt_content: "",
  });
  const [adsenseLogs, setAdsenseLogs] = useState([]);
  const [adsenseStats, setAdsenseStats] = useState({});
  const [showPublisherId, setShowPublisherId] = useState(false);

  // Comms Ledger with classic page-based pagination
  const [commsLogs, setCommsLogs] = useState([]);
  const [commsFilter, setCommsFilter] = useState({ channel: "all", status: "all", recipient: "" });
  const [commsPage, setCommsPage] = useState(1);
  const [commsPageSize] = useState(50);
  const [commsTotal, setCommsTotal] = useState(0);
  const [commsTotalPages, setCommsTotalPages] = useState(1);
  const [commsLoading, setCommsLoading] = useState(false);
  const [emailEvents, setEmailEvents] = useState([]);
  const [eventStats, setEventStats] = useState({});
  const [testEmail, setTestEmail] = useState("");
  const [testPhone, setTestPhone] = useState("");

  // Payment logs
  const [paymentLogs, setPaymentLogs] = useState([]);
  const [paymentStats, setPaymentStats] = useState({});
  const [paymentLogFilter, setPaymentLogFilter] = useState({ event_type: "all", result: "all" });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactionEligibility, setTransactionEligibility] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [processingAction, setProcessingAction] = useState(false);

  // Admin Job Runner state
  const [jobRunning, setJobRunning] = useState(null); // "appointment_reminders" | "invoice_followups" | null
  const [jobResults, setJobResults] = useState({});

  useEffect(() => {
    fetchSettings();
    fetchCommsLogs();
    fetchEmailEvents();
    fetchPaymentLogs();
    fetchAdsenseLogs();
  }, []);

  const fetchSettings = async () => {
    try {
      const [sgRes, tcRes, twRes, trRes, adRes, finixRes, webhookRes] = await Promise.all([
        fetch(`${API}/admin/system/sendgrid`, { credentials: "include" }),
        fetch(`${API}/admin/system/trustcommerce`, { credentials: "include" }),
        fetch(`${API}/admin/system/twilio`, { credentials: "include" }),
        fetch(`${API}/admin/system/translation`, { credentials: "include" }),
        fetch(`${API}/admin/system/adsense`, { credentials: "include" }),
        fetch(`${API}/admin/system/finix`, { credentials: "include" }),
        fetch(`${API}/admin/system/finix/webhook-status`, { credentials: "include" }),
      ]);
      
      if (sgRes.ok) {
        const data = await sgRes.json();
        setSendgridSettings(prev => ({ ...prev, ...data, api_key: "" }));
      }
      if (tcRes.ok) {
        const data = await tcRes.json();
        // Map backend fields to state, keeping credential fields empty for security
        setTcSettings(prev => ({ 
          ...prev, 
          ...data,
          custid: "",  // Don't prefill actual credential
          password: "", 
          licensekey: "" 
        }));
      }
      if (twRes.ok) {
        const data = await twRes.json();
        setTwilioSettings(prev => ({ ...prev, ...data, account_sid: "", auth_token: "" }));
      }
      if (trRes.ok) {
        const data = await trRes.json();
        setTranslationSettings(prev => ({ ...prev, ...data }));
      }
      if (adRes.ok) {
        const data = await adRes.json();
        setAdsenseSettings(prev => ({ ...prev, ...data, adsense_publisher_id: "" }));
      }
      if (finixRes.ok) {
        const data = await finixRes.json();
        // Handle new dual-env structure
        const finixData = data.finix || {};
        setFinixSettings(prev => ({ 
          ...prev,
          enabled: finixData.enabled ?? data.enabled ?? false,
          active_environment: finixData.active_environment || "sandbox",
          use_dual_env_resolver: finixData.use_dual_env_resolver ?? false,
          sandbox: {
            merchant_id: finixData.sandbox?.merchant_id || "",
            application_id: finixData.sandbox?.application_id || "",
            username: "",  // Never prefill credentials - write-only
            password: "",
            username_masked: finixData.sandbox?.username_masked || null,
            username_present: finixData.sandbox?.username_present ?? false,
            password_present: finixData.sandbox?.password_present ?? false,
          },
          production: {
            merchant_id: finixData.production?.merchant_id || "",
            application_id: finixData.production?.application_id || "",
            username: "",
            password: "",
            username_masked: finixData.production?.username_masked || null,
            username_present: finixData.production?.username_present ?? false,
            password_present: finixData.production?.password_present ?? false,
          },
          version: finixData.version || 0,
          legacy_detected: finixData.legacy_detected || false,
          health_preview: data.health_preview || null,
          // Legacy flat fields for backwards compatibility
          environment: data.environment || finixData.active_environment || "sandbox",
          merchant_id: data.merchant_id || "",
          application_id: data.application_id || "",
          configured: data.configured ?? false,
          has_username: data.api_key_configured ?? false,
          has_password: data.api_secret_configured ?? false,
          username: "",
          password: "",
        }));
        
      }
      if (webhookRes.ok) {
        const data = await webhookRes.json();
        setFinixWebhookStatus(data);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommsLogs = async (page = 1) => {
    try {
      setCommsLoading(true);
      
      let url = `${API}/admin/system/email-logs?page=${page}&page_size=${commsPageSize}`;
      if (commsFilter.channel !== "all") {
        url += `&channel=${commsFilter.channel}`;
      }
      if (commsFilter.status !== "all") {
        url += `&status=${commsFilter.status}`;
      }
      if (commsFilter.recipient) {
        url += `&recipient=${encodeURIComponent(commsFilter.recipient)}`;
      }
      
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setCommsLogs(data.items || []);
        setCommsPage(data.page || 1);
        setCommsTotal(data.total || 0);
        setCommsTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      console.error("Error fetching comms logs:", error);
    } finally {
      setCommsLoading(false);
    }
  };

  // Refresh comms when filter changes (reset to page 1)
  useEffect(() => {
    setCommsPage(1);
    fetchCommsLogs(1);
  }, [commsFilter.channel, commsFilter.status]);

  const fetchEmailEvents = async () => {
    try {
      const response = await fetch(`${API}/admin/system/email-events?limit=20`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setEmailEvents(data.events || []);
        setEventStats(data.stats || {});
      }
    } catch (error) {
      console.error("Error fetching email events:", error);
    }
  };

  const fetchPaymentLogs = async () => {
    try {
      let url = `${API}/admin/system/payment-logs?limit=50`;
      if (paymentLogFilter.event_type !== "all") {
        url += `&event_type=${paymentLogFilter.event_type}`;
      }
      if (paymentLogFilter.result !== "all") {
        url += `&result=${paymentLogFilter.result}`;
      }
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setPaymentLogs(data.logs || []);
        setPaymentStats(data.stats || {});
      }
    } catch (error) {
      console.error("Error fetching payment logs:", error);
    }
  };

  useEffect(() => {
    if (activeTab === "payment-logs") {
      fetchPaymentLogs();
    }
  }, [paymentLogFilter, activeTab]);

  // Check transaction eligibility for refund/void
  const checkTransactionEligibility = async (transactionId) => {
    try {
      const response = await fetch(`${API}/admin/system/payments/${transactionId}/eligibility`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setTransactionEligibility(data);
        return data;
      }
    } catch (error) {
      console.error("Error checking eligibility:", error);
    }
    return null;
  };

  // Open transaction details modal
  const openDetailsModal = async (transaction) => {
    setSelectedTransaction(transaction);
    // If this is a successful charge, check eligibility
    if (transaction.result === "success" && 
        (transaction.event_type?.includes("charge") || transaction.event_type === "charge_stored_card")) {
      // Use internal_transaction_id (enriched from backend) or correlation_id
      // These map to transaction_id in billing_transactions collection
      const internalTxnId = transaction.internal_transaction_id || 
                           transaction.correlation_id || 
                           transaction.original_transaction_id;
      if (internalTxnId) {
        const eligibility = await checkTransactionEligibility(internalTxnId);
        setTransactionEligibility(eligibility);
      } else {
        // No internal transaction ID - legacy row
        setTransactionEligibility({
          can_refund: false,
          can_void: false,
          reason: "Refund/Void unavailable: gateway transaction ID not stored",
          has_tc_transid: false
        });
      }
    } else {
      setTransactionEligibility(null);
    }
    setShowDetailsModal(true);
  };

  // Process refund
  const processRefund = async () => {
    if (!selectedTransaction) return;
    
    setProcessingAction(true);
    try {
      // Use internal_transaction_id (the key to billing_transactions collection)
      const transactionId = selectedTransaction.internal_transaction_id || 
                           selectedTransaction.correlation_id ||
                           selectedTransaction.original_transaction_id;
      
      if (!transactionId) {
        toast.error("Cannot process refund: missing transaction ID");
        setProcessingAction(false);
        return;
      }
      
      const body = refundAmount ? { amount_cents: Math.round(parseFloat(refundAmount) * 100) } : {};
      
      const response = await fetch(`${API}/admin/system/payments/${transactionId}/refund`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Refund processed: ${data.amount_formatted}`);
        setShowRefundModal(false);
        setShowDetailsModal(false);
        setRefundAmount("");
        fetchPaymentLogs();
      } else {
        toast.error(data.detail || "Refund failed");
      }
    } catch (error) {
      toast.error("Error processing refund");
      console.error(error);
    } finally {
      setProcessingAction(false);
    }
  };

  // Process void
  const processVoid = async () => {
    if (!selectedTransaction) return;
    
    setProcessingAction(true);
    try {
      // Use internal_transaction_id (the key to billing_transactions collection)
      const transactionId = selectedTransaction.internal_transaction_id || 
                           selectedTransaction.correlation_id ||
                           selectedTransaction.original_transaction_id;
      
      if (!transactionId) {
        toast.error("Cannot process void: missing transaction ID");
        setProcessingAction(false);
        return;
      }
      
      const response = await fetch(`${API}/admin/system/payments/${transactionId}/void`, {
        method: "POST",
        credentials: "include"
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Transaction voided: ${data.amount_formatted}`);
        setShowVoidModal(false);
        setShowDetailsModal(false);
        fetchPaymentLogs();
      } else {
        toast.error(data.detail || "Void failed");
      }
    } catch (error) {
      toast.error("Error processing void");
      console.error(error);
    } finally {
      setProcessingAction(false);
    }
  };

  // Format amount from cents
  const formatAmount = (cents) => {
    if (!cents && cents !== 0) return "-";
    return `$${(cents / 100).toFixed(2)}`;
  };

  // SendGrid handlers
  const saveSendGridSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/system/sendgrid`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(sendgridSettings),
      });
      if (response.ok) {
        toast.success("SendGrid settings saved");
        fetchSettings();
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const testSendGrid = async () => {
    setTesting(true);
    try {
      const url = testEmail 
        ? `${API}/admin/system/sendgrid/test?recipient_email=${encodeURIComponent(testEmail)}`
        : `${API}/admin/system/sendgrid/test`;
      const response = await fetch(url, { method: "POST", credentials: "include" });
      const data = await response.json();
      if (data.success) {
        toast.success(`Test email sent to ${data.sent_to}!`);
      } else {
        toast.error(`Test failed: ${data.result?.message || data.message || "Unknown error"}`);
      }
    } catch (error) {
      toast.error("Failed to send test email");
    } finally {
      setTesting(false);
    }
  };

  // TrustCommerce handlers
  const handleTCCustIdChange = (value) => {
    // Allow only numeric input
    const numericValue = value.replace(/[^0-9]/g, '');
    setTcSettings({ ...tcSettings, custid: numericValue });
    
    // Validate: must be numeric (or empty if just clearing)
    if (value && value !== numericValue) {
      setTcCustIdError("Customer ID must be numeric only");
    } else {
      setTcCustIdError("");
    }
  };

  const saveTCSettings = async () => {
    // Frontend validation: All required fields must be provided when saving credentials
    const hasNewCredentials = tcSettings.custid || tcSettings.password || tcSettings.licensekey;
    
    if (hasNewCredentials) {
      // Validate custid is numeric
      if (tcSettings.custid && !/^\d+$/.test(tcSettings.custid)) {
        toast.error("Customer ID must be numeric only");
        setTcCustIdError("Customer ID must be numeric only");
        return;
      }
      
      // If providing new custid, also require password and licensekey (unless already configured)
      if (tcSettings.custid && !tcSettings.password && !tcSettings.password_configured) {
        toast.error("API Password is required");
        return;
      }
      if (tcSettings.custid && !tcSettings.licensekey && !tcSettings.licensekey_configured) {
        toast.error("License Key is required for Premier hosted forms");
        return;
      }
    }
    
    setSaving(true);
    try {
      // Build payload - only include non-empty credential fields
      const payload = {
        mode: tcSettings.mode,
        vault_enabled: tcSettings.vault_enabled,
        avs_enabled: tcSettings.avs_enabled,
        cvv_enabled: tcSettings.cvv_enabled,
      };
      
      // Only include credentials if user entered new values
      if (tcSettings.custid) payload.custid = tcSettings.custid;
      if (tcSettings.password) payload.password = tcSettings.password;
      if (tcSettings.licensekey) payload.licensekey = tcSettings.licensekey;
      
      const response = await fetch(`${API}/admin/system/trustcommerce`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        toast.success("TrustCommerce configuration saved");
        // Clear input fields and refresh state from server
        setTcSettings(prev => ({ ...prev, custid: "", password: "", licensekey: "" }));
        fetchSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save settings");
        console.error("[TC Config] Save failed:", error.detail);
      }
    } catch (error) {
      toast.error("Failed to save settings");
      console.error("[TC Config] Save error:", error);
    } finally {
      setSaving(false);
    }
  };

  const testTrustCommerce = async () => {
    setTesting(true);
    try {
      const response = await fetch(`${API}/admin/system/trustcommerce/test`, {
        method: "POST",
        credentials: "include",
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success("✓ Credentials verified with TrustCommerce");
        // Refresh settings to get updated test_status
        fetchSettings();
      } else {
        toast.error(data.message || "Test failed");
        console.error("[TC Config] Test failed:", data);
        // Still refresh to show failed status
        fetchSettings();
      }
    } catch (error) {
      toast.error("Failed to test TrustCommerce connection");
      console.error("[TC Config] Test error:", error);
    } finally {
      setTesting(false);
    }
  };

  // Twilio handlers
  const saveTwilioSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/system/twilio`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(twilioSettings),
      });
      if (response.ok) {
        toast.success("Twilio settings saved");
        fetchSettings();
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const testTwilio = async () => {
    if (!testPhone) {
      toast.error("Please enter a phone number to test");
      return;
    }
    
    setTesting(true);
    try {
      const response = await fetch(`${API}/admin/system/twilio/test?phone_number=${encodeURIComponent(testPhone)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const data = await response.json();
      
      // Use standardized 'ok' field for success check
      if (data.ok === true) {
        if (data.dev_mode) {
          toast.success("Test SMS logged (dev mode - not actually sent)");
        } else {
          toast.success(`Test SMS sent successfully! SID: ${data.provider_message_id?.substring(0, 12)}...`);
        }
        // Refresh settings to show updated test status
        fetchSettings();
      } else {
        // Show specific error message from backend
        toast.error(`Failed to test Twilio: ${data.error_message || "Unknown error"}`);
      }
    } catch (error) {
      toast.error("Failed to test Twilio: Network error");
    } finally {
      setTesting(false);
    }
  };

  // Translation handlers
  const saveTranslationSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/system/translation`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(translationSettings),
      });
      if (response.ok) {
        toast.success("Translation settings saved");
        fetchSettings();
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const testTranslation = async () => {
    setTesting(true);
    setTranslationResult(null);
    try {
      const response = await fetch(`${API}/admin/system/translation/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ test_text: testText, target_language: translationSettings.target_language }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Translation successful!");
        setTranslationResult(data);
      } else {
        toast.error(data.message || "Translation failed");
      }
    } catch (error) {
      toast.error("Failed to test translation");
    } finally {
      setTesting(false);
    }
  };

  // AdSense handlers
  const fetchAdsenseLogs = async () => {
    try {
      const response = await fetch(`${API}/admin/system/adsense/logs?limit=30`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setAdsenseLogs(data.logs || []);
        setAdsenseStats(data.stats || {});
      }
    } catch (error) {
      console.error("Error fetching AdSense logs:", error);
    }
  };

  const saveAdsenseSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/system/adsense`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(adsenseSettings),
      });
      if (response.ok) {
        toast.success("AdSense settings saved - changes take effect immediately");
        fetchSettings();
      } else {
        toast.error("Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const addAdUnit = () => {
    setAdsenseSettings(prev => ({
      ...prev,
      ad_units: [
        ...prev.ad_units,
        { name: "", placement_key: "dashboard_footer", ad_slot_id: "", format: "auto" }
      ]
    }));
  };

  const updateAdUnit = (index, field, value) => {
    setAdsenseSettings(prev => ({
      ...prev,
      ad_units: prev.ad_units.map((unit, i) => 
        i === index ? { ...unit, [field]: value } : unit
      )
    }));
  };

  const removeAdUnit = (index) => {
    setAdsenseSettings(prev => ({
      ...prev,
      ad_units: prev.ad_units.filter((_, i) => i !== index)
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const updateEmailType = (type, field, value) => {
    setSendgridSettings({
      ...sendgridSettings,
      email_types: {
        ...sendgridSettings.email_types,
        [type]: { ...sendgridSettings.email_types[type], [field]: value },
      },
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const colors = {
      sent: "bg-emerald-100 text-emerald-800",
      failed: "bg-red-100 text-red-800",
      dev_mode: "bg-blue-100 text-blue-800",
      skipped: "bg-zinc-100 text-zinc-600",
    };
    return <Badge className={colors[status] || "bg-zinc-100"}>{status}</Badge>;
  };

  const getEventBadge = (eventType) => {
    const colors = {
      delivered: "bg-emerald-100 text-emerald-800",
      open: "bg-blue-100 text-blue-800",
      click: "bg-purple-100 text-purple-800",
      bounce: "bg-red-100 text-red-800",
      spamreport: "bg-orange-100 text-orange-800",
      dropped: "bg-red-100 text-red-800",
    };
    return <Badge className={colors[eventType] || "bg-zinc-100"}>{eventType}</Badge>;
  };

  // Admin Job Runner
  const runAdminJob = async (jobKey, dryRun) => {
    setJobRunning(`${jobKey}_${dryRun ? "dry" : "live"}`);
    try {
      const response = await fetch(`${API}/admin/jobs/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ job_key: jobKey, dry_run: dryRun }),
      });
      const data = await response.json();
      if (response.ok) {
        setJobResults(prev => ({ ...prev, [jobKey]: data }));
        toast.success(`${dryRun ? "Dry run" : "Job"} complete: ${data.sent_count || 0} sent`);
      } else {
        toast.error(data.detail || "Job failed");
        setJobResults(prev => ({ ...prev, [jobKey]: { error: data.detail } }));
      }
    } catch (error) {
      toast.error("Failed to run job");
      setJobResults(prev => ({ ...prev, [jobKey]: { error: error.message } }));
    } finally {
      setJobRunning(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-48 bg-zinc-700 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-system-utilities">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">System Utilities</h1>
          <p className="text-zinc-400">Manage platform-wide integrations and settings</p>
        </div>
      </div>

      {/* System Recovery Panel - Platform safety controls */}
      <SystemRecoveryPanel />

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="bg-zinc-800 border border-zinc-700 flex-wrap">
          {/* === OPERATIONS === */}
          <TabsTrigger value="comms-ledger" className="text-zinc-300 hover:text-white data-[state=active]:bg-zinc-700 data-[state=active]:text-white" data-testid="comms-ledger-tab">
            <ListBullets size={16} className="mr-2" />
            Comms
          </TabsTrigger>
          <TabsTrigger value="payment-logs" className="text-zinc-300 hover:text-white data-[state=active]:bg-zinc-700 data-[state=active]:text-white" data-testid="payment-logs-tab">
            <CreditCard size={16} className="mr-2" />
            Payments
          </TabsTrigger>
          {ENABLE_ADMIN_JOB_RUNNER && (
            <TabsTrigger value="jobs" className="text-zinc-300 hover:text-white data-[state=active]:bg-zinc-700 data-[state=active]:text-white" data-testid="jobs-tab">
              <Lightning size={16} className="mr-2" />
              Jobs
            </TabsTrigger>
          )}
          
          {/* === BILLING === */}
          <TabsTrigger value="billing-ops" className="text-zinc-300 hover:text-white data-[state=active]:bg-zinc-700 data-[state=active]:text-white" data-testid="billing-ops-tab">
            <Wallet size={16} className="mr-2" />
            Billing
          </TabsTrigger>
          
          {/* === INTEGRATIONS === */}
          {/* TrustCommerce tab - DEPRECATED: Hidden during Finix migration */}
          {ENABLE_TRUSTCOMMERCE_TAB && (
            <TabsTrigger value="trustcommerce" className="text-zinc-300 hover:text-white data-[state=active]:bg-zinc-700 data-[state=active]:text-white">
              <CreditCard size={16} className="mr-2" />
              TrustCommerce
            </TabsTrigger>
          )}
          <TabsTrigger value="finix" className="text-zinc-300 hover:text-white data-[state=active]:bg-zinc-700 data-[state=active]:text-white">
            <CreditCard size={16} className="mr-2" />
            Finix
            {finixSettings.configured && (
              <CheckCircle size={12} className="ml-1 text-emerald-400" />
            )}
          </TabsTrigger>
          <TabsTrigger value="sendgrid" className="text-zinc-300 hover:text-white data-[state=active]:bg-zinc-700 data-[state=active]:text-white">
            <Envelope size={16} className="mr-2" />
            SendGrid
          </TabsTrigger>
          <TabsTrigger value="twilio" className="text-zinc-300 hover:text-white data-[state=active]:bg-zinc-700 data-[state=active]:text-white">
            <ChatCircleText size={16} className="mr-2" />
            Twilio
          </TabsTrigger>
          <TabsTrigger value="translation" className="text-zinc-300 hover:text-white data-[state=active]:bg-zinc-700 data-[state=active]:text-white">
            <Translate size={16} className="mr-2" />
            Translation
          </TabsTrigger>
          
          {/* === CONFIGURATION === */}
          <TabsTrigger value="service-types" className="text-zinc-300 hover:text-white data-[state=active]:bg-zinc-700 data-[state=active]:text-white" data-testid="service-types-tab">
            <ListBullets size={16} className="mr-2" />
            Service Types
          </TabsTrigger>
          
          {/* === INTERNAL / EXPERIMENTAL (hidden by default) === */}
          {ENABLE_ADVERTISING_ADMIN && (
            <TabsTrigger value="adsense" className="text-zinc-300 hover:text-white data-[state=active]:bg-zinc-700 data-[state=active]:text-white" data-testid="advertising-tab">
              <Megaphone size={16} className="mr-2" />
              Advertising
            </TabsTrigger>
          )}
          {ENABLE_WEBHOOKS_ADMIN && (
            <TabsTrigger value="events" className="text-zinc-300 hover:text-white data-[state=active]:bg-zinc-700 data-[state=active]:text-white" data-testid="webhooks-tab">
              <PaperPlaneTilt size={16} className="mr-2" />
              Webhooks
            </TabsTrigger>
          )}
          {ENABLE_CUSTOM_ADS_ADMIN && (
            <TabsTrigger value="custom_ads" className="text-zinc-300 hover:text-white data-[state=active]:bg-zinc-700 data-[state=active]:text-white" data-testid="custom-ads-tab">
              <Megaphone size={16} className="mr-2" />
              Custom Ads
            </TabsTrigger>
          )}
        </TabsList>


        {/* Billing Ops Panel */}
        <TabsContent value="billing-ops">
          <AdminBillingOps />
        </TabsContent>

        {/* SendGrid Settings */}
        <TabsContent value="sendgrid">
          <div className="space-y-6">
            <Card className="bg-zinc-800 border-zinc-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Envelope size={20} />
                      SendGrid Configuration
                    </CardTitle>
                    <CardDescription className="text-zinc-400">
                      Configure transactional email delivery
                    </CardDescription>
                  </div>
                  {sendgridSettings.api_key_configured ? (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      <CheckCircle size={14} className="mr-1" />
                      Connected
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                      <Warning size={14} className="mr-1" />
                      Not Configured
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-zinc-300">SendGrid API Key</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showApiKey ? "text" : "password"}
                      value={sendgridSettings.api_key}
                      onChange={(e) => setSendgridSettings({ ...sendgridSettings, api_key: e.target.value })}
                      placeholder={sendgridSettings.api_key_masked || "SG.xxx..."}
                      className="bg-zinc-900 border-zinc-600 text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400"
                    >
                      {showApiKey ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    Get your API key from{" "}
                    <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-[#FF6600] hover:underline">
                      SendGrid Dashboard
                    </a>
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">From Email</Label>
                    <Input
                      type="email"
                      value={sendgridSettings.from_email}
                      onChange={(e) => setSendgridSettings({ ...sendgridSettings, from_email: e.target.value })}
                      placeholder="noreply@yourdomain.com"
                      className="mt-1 bg-zinc-900 border-zinc-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">From Name</Label>
                    <Input
                      value={sendgridSettings.from_name}
                      onChange={(e) => setSendgridSettings({ ...sendgridSettings, from_name: e.target.value })}
                      placeholder="Free Service Pro"
                      className="mt-1 bg-zinc-900 border-zinc-600 text-white"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-zinc-900 rounded-lg">
                  <div>
                    <Label className="text-zinc-300">Environment Mode</Label>
                    <p className="text-xs text-zinc-500 mt-1">
                      {sendgridSettings.environment === "production" ? "Emails will be sent to real recipients" : "Emails will be logged but not sent"}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-zinc-400">Dev</span>
                    <Switch
                      checked={sendgridSettings.environment === "production"}
                      onCheckedChange={(checked) => setSendgridSettings({ ...sendgridSettings, environment: checked ? "production" : "development" })}
                    />
                    <span className="text-sm text-zinc-400">Prod</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-800 border-zinc-700">
              <CardHeader>
                <CardTitle className="text-white text-base">Test Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-zinc-300">Test Email Recipient</Label>
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                    className="mt-1 bg-zinc-900 border-zinc-600 text-white"
                  />
                </div>
                <div className="flex gap-3">
                  <Button onClick={saveSendGridSettings} disabled={saving} className="bg-[#FF6600] hover:bg-[#CC5200] text-white">
                    {saving ? "Saving..." : "Save Settings"}
                  </Button>
                  <Button variant="outline" onClick={testSendGrid} disabled={testing || !sendgridSettings.api_key_configured} className="border-zinc-600 text-zinc-300 hover:bg-zinc-700">
                    {testing ? <><ArrowClockwise size={16} className="mr-2 animate-spin" />Sending...</> : <><PaperPlaneTilt size={16} className="mr-2" />Send Test</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TrustCommerce Settings */}
        <TabsContent value="trustcommerce">
          <div className="space-y-6">
            <Card className="bg-zinc-800 border-zinc-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <CreditCard size={20} />
                      TrustCommerce Premier Configuration
                    </CardTitle>
                    <CardDescription className="text-zinc-400">
                      Configure payment processing for contractors (SAQ-A compliant hosted form)
                    </CardDescription>
                  </div>
                  {/* Status Badge: "Configured" only when all fields saved AND test passed */}
                  {tcSettings.custid_configured && tcSettings.password_configured && tcSettings.licensekey_configured && tcSettings.test_status === "success" ? (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      <CheckCircle size={14} className="mr-1" />
                      Configured
                    </Badge>
                  ) : tcSettings.custid_configured && tcSettings.password_configured && tcSettings.licensekey_configured ? (
                    <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                      <Info size={14} className="mr-1" />
                      Saved - Test Required
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                      <Warning size={14} className="mr-1" />
                      Not Configured
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Required Credentials Info */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex gap-3">
                    <Info size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-200">
                      <p className="font-medium mb-1">Required for Premier Hosted Form</p>
                      <p className="text-blue-300/80">
                        All three credentials below are <strong>required</strong> for the TrustCommerce Premier 
                        hosted payment form to work. The Customer ID must be numeric.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Last Test Status */}
                {tcSettings.last_test && (
                  <div className={`p-3 rounded-lg ${tcSettings.test_status === "success" ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-red-500/10 border border-red-500/30"}`}>
                    <p className={`text-sm ${tcSettings.test_status === "success" ? "text-emerald-300" : "text-red-300"}`}>
                      {tcSettings.test_status === "success" ? "✓ " : "✗ "}
                      Last test: {tcSettings.test_status === "success" ? "Connection verified" : "Connection failed"}
                      {tcSettings.last_test && ` (${new Date(tcSettings.last_test).toLocaleString()})`}
                    </p>
                  </div>
                )}

                {/* Processing Mode - Required first */}
                <div>
                  <Label className="text-zinc-300">Processing Mode <span className="text-red-400">*</span></Label>
                  <Select value={tcSettings.mode} onValueChange={(value) => setTcSettings({ ...tcSettings, mode: value })}>
                    <SelectTrigger className="mt-1 bg-zinc-900 border-zinc-600 text-white" data-testid="tc-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="test">Test (Test transactions only)</SelectItem>
                      <SelectItem value="production">Production (Live transactions)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500 mt-1">
                    {tcSettings.mode === "test" 
                      ? "Test mode uses TrustCommerce's test environment. No real charges." 
                      : "⚠️ Production mode processes REAL transactions with real money."}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">
                      Customer ID (custid) <span className="text-red-400">*</span>
                      {tcSettings.custid_configured && <span className="ml-2 text-emerald-400 text-xs">(saved)</span>}
                    </Label>
                    <div className="relative mt-1">
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={tcSettings.custid}
                        onChange={(e) => handleTCCustIdChange(e.target.value)}
                        placeholder={tcSettings.custid_masked || "Enter numeric Customer ID"}
                        className={`bg-zinc-900 border-zinc-600 text-white ${tcCustIdError ? 'border-red-500' : ''}`}
                        data-testid="tc-custid-input"
                      />
                    </div>
                    {tcCustIdError && (
                      <p className="text-xs text-red-400 mt-1">{tcCustIdError}</p>
                    )}
                    <p className="text-xs text-zinc-500 mt-1">
                      Your TrustCommerce merchant ID (numeric only)
                      {tcSettings.custid_masked && <span className="ml-1 text-zinc-400">Current: {tcSettings.custid_masked}</span>}
                    </p>
                  </div>
                  <div>
                    <Label className="text-zinc-300">
                      API Password <span className="text-red-400">*</span>
                      {tcSettings.password_configured && <span className="ml-2 text-emerald-400 text-xs">(saved)</span>}
                    </Label>
                    <div className="relative mt-1">
                      <Input
                        type={showTCPassword ? "text" : "password"}
                        value={tcSettings.password}
                        onChange={(e) => setTcSettings({ ...tcSettings, password: e.target.value })}
                        placeholder={tcSettings.password_configured ? "••••••••" : "Enter API password"}
                        className="bg-zinc-900 border-zinc-600 text-white pr-10"
                        data-testid="tc-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowTCPassword(!showTCPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400"
                      >
                        {showTCPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* License Key - REQUIRED FIELD */}
                <div>
                  <Label className="text-zinc-300">
                    License Key <span className="text-red-400">*</span>
                    {tcSettings.licensekey_configured && <span className="ml-2 text-emerald-400 text-xs">(saved)</span>}
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      type={showTCLicenseKey ? "text" : "password"}
                      value={tcSettings.licensekey}
                      onChange={(e) => setTcSettings({ ...tcSettings, licensekey: e.target.value })}
                      placeholder={tcSettings.licensekey_configured ? "••••••••" : "Enter Premier license key"}
                      className="bg-zinc-900 border-zinc-600 text-white pr-10"
                      data-testid="tc-licensekey-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowTCLicenseKey(!showTCLicenseKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400"
                    >
                      {showTCLicenseKey ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    Required for Premier hosted form & Vault tokenization. Get this from your TrustCommerce account.
                  </p>
                </div>

                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-zinc-300">Security Options</h4>
                  <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
                    <div>
                      <Label className="text-zinc-300">Vault (Card Storage)</Label>
                      <p className="text-xs text-zinc-500">Store cards securely for repeat billing</p>
                    </div>
                    <Switch checked={tcSettings.vault_enabled} onCheckedChange={(checked) => setTcSettings({ ...tcSettings, vault_enabled: checked })} />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
                    <div>
                      <Label className="text-zinc-300">AVS Check</Label>
                      <p className="text-xs text-zinc-500">Address verification for fraud prevention</p>
                    </div>
                    <Switch checked={tcSettings.avs_enabled} onCheckedChange={(checked) => setTcSettings({ ...tcSettings, avs_enabled: checked })} />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
                    <div>
                      <Label className="text-zinc-300">CVV Required</Label>
                      <p className="text-xs text-zinc-500">Require security code on all transactions</p>
                    </div>
                    <Switch checked={tcSettings.cvv_enabled} onCheckedChange={(checked) => setTcSettings({ ...tcSettings, cvv_enabled: checked })} />
                  </div>
                </div>

                <div className="pt-4 border-t border-zinc-700">
                  <p className="text-xs text-zinc-500 mb-4">
                    Get your credentials from{" "}
                    <a href="https://vault.trustcommerce.com/" target="_blank" rel="noopener noreferrer" className="text-[#FF6600] hover:underline">
                      TrustCommerce Vault
                    </a>
                  </p>
                  <div className="flex gap-3">
                    <Button 
                      onClick={saveTCSettings} 
                      disabled={saving || !!tcCustIdError} 
                      className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                      data-testid="tc-save-btn"
                    >
                      {saving ? "Saving..." : "Save Settings"}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={testTrustCommerce} 
                      disabled={testing || !tcSettings.custid_configured || !tcSettings.password_configured || !tcSettings.licensekey_configured} 
                      className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                      data-testid="tc-test-btn"
                    >
                      {testing ? <><ArrowClockwise size={16} className="mr-2 animate-spin" />Testing...</> : <><Gear size={16} className="mr-2" />Test Connection</>}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Finix Settings - New Payment Processor */}
        <TabsContent value="finix">
          <div className="space-y-6">
            <Card className="bg-zinc-800 border-zinc-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <CreditCard size={20} />
                      Finix Payment Configuration
                    </CardTitle>
                    <CardDescription className="text-zinc-400">
                      Configure Finix payment processing (Flex hosted fields)
                    </CardDescription>
                  </div>
                  {finixSettings.configured ? (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      <CheckCircle size={14} className="mr-1" />
                      Configured
                    </Badge>
                  ) : finixSettings.enabled ? (
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                      <Warning size={14} className="mr-1" />
                      Enabled - Incomplete
                    </Badge>
                  ) : (
                    <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30">
                      Disabled
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable Toggle */}
                <div className="flex items-center justify-between p-4 bg-zinc-900 rounded-lg">
                  <div>
                    <Label className="text-zinc-300 text-base">Enable Finix</Label>
                    <p className="text-xs text-zinc-500 mt-1">
                      Enable Finix payment processing for the platform
                    </p>
                  </div>
                  <Switch
                    checked={finixSettings.enabled}
                    onCheckedChange={(checked) => setFinixSettings({ ...finixSettings, enabled: checked })}
                    data-testid="finix-enabled-switch"
                  />
                </div>

                {/* Environment Selection */}
                <div>
                  <Label className="text-zinc-300">Active Environment <span className="text-red-400">*</span></Label>
                  <Select 
                    value={finixSettings.active_environment} 
                    onValueChange={(value) => setFinixSettings({ ...finixSettings, active_environment: value })}
                  >
                    <SelectTrigger className="mt-1 bg-zinc-900 border-zinc-600 text-white" data-testid="finix-environment-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sandbox">Sandbox (Test transactions)</SelectItem>
                      <SelectItem value="production">Production (Real transactions)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500 mt-1">
                    {finixSettings.active_environment === "sandbox" 
                      ? "Sandbox mode for testing. No real charges." 
                      : "⚠️ Production mode processes REAL transactions."}
                  </p>
                </div>

                {/* Kill Switch: Use Dual-Env Resolver */}
                <div className="flex items-center justify-between py-3 px-4 bg-zinc-900 rounded-lg border border-zinc-700">
                  <div>
                    <Label className="text-zinc-200 font-medium">Use Dual-Env Credential Resolver</Label>
                    <p className="text-xs text-zinc-500 mt-1">
                      When enabled, credentials are resolved from the selected environment panel above.
                      {finixSettings.use_dual_env_resolver ? (
                        <span className="text-emerald-400 ml-1">
                          (Active: using {finixSettings.active_environment} credentials)
                        </span>
                      ) : (
                        <span className="text-amber-400 ml-1">(Off: using legacy flat credentials)</span>
                      )}
                    </p>
                  </div>
                  <Switch
                    checked={finixSettings.use_dual_env_resolver}
                    onCheckedChange={(checked) => setFinixSettings({ ...finixSettings, use_dual_env_resolver: checked })}
                    data-testid="finix-use-dual-env-resolver-switch"
                  />
                </div>

                {/* Health Preview Block */}
                {finixSettings.health_preview && (
                  <div className="bg-zinc-900 rounded-lg p-4 border border-zinc-700">
                    <h4 className="text-sm font-medium text-zinc-300 mb-3">Health Preview</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${finixSettings.health_preview.finix_enabled ? 'bg-emerald-500' : 'bg-zinc-500'}`} />
                        <span className="text-zinc-400">Finix Enabled</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${finixSettings.health_preview.sandbox_configured ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                        <span className="text-zinc-400">Sandbox Configured</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${finixSettings.health_preview.production_configured ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                        <span className="text-zinc-400">Production Configured</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${finixSettings.health_preview.tokenization_ready ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        <span className="text-zinc-400">Tokenization Ready</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${finixSettings.health_preview.use_dual_env_resolver ? 'bg-emerald-500' : 'bg-zinc-500'}`} />
                        <span className="text-zinc-400">Dual-Env Resolver</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${finixSettings.health_preview.effective_credential_source === 'dual_env' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                        <span className="text-zinc-400">
                          Source: {finixSettings.health_preview.effective_credential_source || 'legacy'}
                        </span>
                      </div>
                    </div>
                    {finixSettings.health_preview.legacy_detected && (
                      <div className="mt-2 text-xs text-amber-400">
                        ⚠️ Legacy configuration detected. Consider migrating to dual-env format.
                      </div>
                    )}
                  </div>
                )}

                {/* Dual Environment Panels */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Sandbox Panel */}
                  <div className={`p-4 rounded-lg border ${finixSettings.active_environment === 'sandbox' ? 'bg-blue-900/20 border-blue-500/50' : 'bg-zinc-900 border-zinc-700'}`}>
                    <h4 className="text-sm font-medium text-zinc-200 mb-3 flex items-center gap-2">
                      Sandbox Environment
                      {finixSettings.active_environment === 'sandbox' && (
                        <Badge className="bg-blue-600 text-white text-xs">Active</Badge>
                      )}
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-zinc-400 text-xs">Merchant ID</Label>
                        <Input
                          type="text"
                          value={finixSettings.sandbox.merchant_id}
                          onChange={(e) => setFinixSettings(prev => ({
                            ...prev,
                            sandbox: { ...prev.sandbox, merchant_id: e.target.value }
                          }))}
                          placeholder="MUxxxxxxxxxxxxxxxxxx"
                          className="mt-1 bg-zinc-800 border-zinc-600 text-white text-sm"
                          data-testid="finix-sandbox-merchant-id"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Application ID</Label>
                        <Input
                          type="text"
                          value={finixSettings.sandbox.application_id}
                          onChange={(e) => setFinixSettings(prev => ({
                            ...prev,
                            sandbox: { ...prev.sandbox, application_id: e.target.value }
                          }))}
                          placeholder="APxxxxxxxxxxxxxxxxxx"
                          className="mt-1 bg-zinc-800 border-zinc-600 text-white text-sm"
                          data-testid="finix-sandbox-application-id"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">
                          Username
                          {finixSettings.sandbox.username_present && (
                            <span className="ml-2 text-emerald-400">(Saved: {finixSettings.sandbox.username_masked})</span>
                          )}
                        </Label>
                        <Input
                          type="text"
                          value={finixSettings.sandbox.username}
                          onChange={(e) => setFinixSettings(prev => ({
                            ...prev,
                            sandbox: { ...prev.sandbox, username: e.target.value }
                          }))}
                          placeholder={finixSettings.sandbox.username_present ? "Leave blank to keep" : "Enter username"}
                          className="mt-1 bg-zinc-800 border-zinc-600 text-white text-sm"
                          data-testid="finix-sandbox-username"
                          autoComplete="off"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">
                          Password
                          {finixSettings.sandbox.password_present && (
                            <span className="ml-2 text-emerald-400">(Saved)</span>
                          )}
                        </Label>
                        <Input
                          type="password"
                          value={finixSettings.sandbox.password}
                          onChange={(e) => setFinixSettings(prev => ({
                            ...prev,
                            sandbox: { ...prev.sandbox, password: e.target.value }
                          }))}
                          placeholder={finixSettings.sandbox.password_present ? "Leave blank to keep" : "Enter password"}
                          className="mt-1 bg-zinc-800 border-zinc-600 text-white text-sm"
                          data-testid="finix-sandbox-password"
                          autoComplete="new-password"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Production Panel */}
                  <div className={`p-4 rounded-lg border ${finixSettings.active_environment === 'production' ? 'bg-red-900/20 border-red-500/50' : 'bg-zinc-900 border-zinc-700'}`}>
                    <h4 className="text-sm font-medium text-zinc-200 mb-3 flex items-center gap-2">
                      Production Environment
                      {finixSettings.active_environment === 'production' && (
                        <Badge className="bg-red-600 text-white text-xs">Active</Badge>
                      )}
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-zinc-400 text-xs">Merchant ID</Label>
                        <Input
                          type="text"
                          value={finixSettings.production.merchant_id}
                          onChange={(e) => setFinixSettings(prev => ({
                            ...prev,
                            production: { ...prev.production, merchant_id: e.target.value }
                          }))}
                          placeholder="MUxxxxxxxxxxxxxxxxxx"
                          className="mt-1 bg-zinc-800 border-zinc-600 text-white text-sm"
                          data-testid="finix-production-merchant-id"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Application ID</Label>
                        <Input
                          type="text"
                          value={finixSettings.production.application_id}
                          onChange={(e) => setFinixSettings(prev => ({
                            ...prev,
                            production: { ...prev.production, application_id: e.target.value }
                          }))}
                          placeholder="APxxxxxxxxxxxxxxxxxx"
                          className="mt-1 bg-zinc-800 border-zinc-600 text-white text-sm"
                          data-testid="finix-production-application-id"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">
                          Username
                          {finixSettings.production.username_present && (
                            <span className="ml-2 text-emerald-400">(Saved: {finixSettings.production.username_masked})</span>
                          )}
                        </Label>
                        <Input
                          type="text"
                          value={finixSettings.production.username}
                          onChange={(e) => setFinixSettings(prev => ({
                            ...prev,
                            production: { ...prev.production, username: e.target.value }
                          }))}
                          placeholder={finixSettings.production.username_present ? "Leave blank to keep" : "Enter username"}
                          className="mt-1 bg-zinc-800 border-zinc-600 text-white text-sm"
                          data-testid="finix-production-username"
                          autoComplete="off"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">
                          Password
                          {finixSettings.production.password_present && (
                            <span className="ml-2 text-emerald-400">(Saved)</span>
                          )}
                        </Label>
                        <Input
                          type="password"
                          value={finixSettings.production.password}
                          onChange={(e) => setFinixSettings(prev => ({
                            ...prev,
                            production: { ...prev.production, password: e.target.value }
                          }))}
                          placeholder={finixSettings.production.password_present ? "Leave blank to keep" : "Enter password"}
                          className="mt-1 bg-zinc-800 border-zinc-600 text-white text-sm"
                          data-testid="finix-production-password"
                          autoComplete="new-password"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex gap-3">
                    <Info size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-200">
                      <p className="font-medium mb-1">Finix HTTP Basic Auth</p>
                      <p className="text-blue-300/80">
                        Finix uses HTTP Basic Authentication. Enter your Finix dashboard credentials.
                        Credentials are stored securely and never returned to the frontend.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-zinc-700">
                  <Button
                    onClick={async () => {
                      setSavingFinix(true);
                      try {
                        // Get the active environment config
                        const activeEnv = finixSettings.active_environment;
                        const activeConfig = activeEnv === "production" 
                          ? finixSettings.production 
                          : finixSettings.sandbox;
                        
                        // Build dual-env payload
                        const payload = {
                          enabled: finixSettings.enabled,
                          active_environment: activeEnv,
                          use_dual_env_resolver: finixSettings.use_dual_env_resolver,
                        };
                        
                        // Build sandbox config (only include credentials if typed)
                        payload.sandbox = {
                          merchant_id: finixSettings.sandbox.merchant_id || undefined,
                          application_id: finixSettings.sandbox.application_id || undefined,
                        };
                        if (finixSettings.sandbox.username?.trim()) {
                          payload.sandbox.username = finixSettings.sandbox.username.trim();
                        }
                        if (finixSettings.sandbox.password?.trim()) {
                          payload.sandbox.password = finixSettings.sandbox.password.trim();
                        }
                        
                        // Build production config (only include credentials if typed)
                        payload.production = {
                          merchant_id: finixSettings.production.merchant_id || undefined,
                          application_id: finixSettings.production.application_id || undefined,
                        };
                        if (finixSettings.production.username?.trim()) {
                          payload.production.username = finixSettings.production.username.trim();
                        }
                        if (finixSettings.production.password?.trim()) {
                          payload.production.password = finixSettings.production.password.trim();
                        }
                        
                        const response = await fetch(`${API}/admin/system/finix`, {
                          method: "PUT",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify(payload),
                        });
                        
                        const parsed = await parseResponseSafely(response);
                        
                        if (parsed.ok) {
                          // Refresh settings to get updated status
                          const refreshRes = await fetch(`${API}/admin/system/finix`, { credentials: "include" });
                          const refreshParsed = await parseResponseSafely(refreshRes);
                          if (refreshParsed.ok && refreshParsed.data) {
                            const refreshData = refreshParsed.data;
                            const finixData = refreshData.finix || {};
                            setFinixSettings(prev => ({ 
                              ...prev,
                              enabled: finixData.enabled ?? refreshData.enabled ?? false,
                              active_environment: finixData.active_environment || "sandbox",
                              use_dual_env_resolver: finixData.use_dual_env_resolver ?? false,
                              sandbox: {
                                merchant_id: finixData.sandbox?.merchant_id || "",
                                application_id: finixData.sandbox?.application_id || "",
                                username: "",
                                password: "",
                                username_masked: finixData.sandbox?.username_masked || null,
                                username_present: finixData.sandbox?.username_present ?? false,
                                password_present: finixData.sandbox?.password_present ?? false,
                              },
                              production: {
                                merchant_id: finixData.production?.merchant_id || "",
                                application_id: finixData.production?.application_id || "",
                                username: "",
                                password: "",
                                username_masked: finixData.production?.username_masked || null,
                                username_present: finixData.production?.username_present ?? false,
                                password_present: finixData.production?.password_present ?? false,
                              },
                              version: finixData.version || 0,
                              legacy_detected: finixData.legacy_detected || false,
                              health_preview: refreshData.health_preview || null,
                              environment: refreshData.environment || "sandbox",
                              merchant_id: refreshData.merchant_id || "",
                              application_id: refreshData.application_id || "",
                              configured: refreshData.configured ?? false,
                              has_username: refreshData.api_key_configured ?? false,
                              has_password: refreshData.api_secret_configured ?? false,
                              username: "",
                              password: "",
                            }));
                          }
                          const successMsg = parsed.data?.warning 
                            ? `Saved with warning: ${parsed.data.warning}` 
                            : "Finix settings saved successfully!";
                          toast.success(successMsg);
                        } else {
                          const errorDetail = parsed.data?.detail;
                          let errorMsg = "Failed to save";
                          if (typeof errorDetail === "object" && errorDetail?.message) {
                            errorMsg = errorDetail.message;
                          } else if (typeof errorDetail === "string") {
                            errorMsg = errorDetail;
                          } else if (parsed.error) {
                            errorMsg = parsed.error;
                          }
                          toast.error(`Error: ${errorMsg}`);
                        }
                      } catch (error) {
                        console.error("Error saving Finix settings:", error);
                        toast.error("Error saving settings");
                      } finally {
                        setSavingFinix(false);
                      }
                    }}
                    disabled={savingFinix}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="finix-save-btn"
                  >
                    {savingFinix ? "Saving..." : "Save Finix Settings"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Finix Webhook Receiver Card */}
            <Card className="bg-zinc-800 border-zinc-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <PaperPlaneTilt size={20} />
                      Finix Webhook Receiver (Disputes/Refund Sync)
                    </CardTitle>
                    <CardDescription className="text-zinc-400">
                      Receive real-time updates from Finix for disputes and refunds
                    </CardDescription>
                  </div>
                  {/* Ready badge */}
                  {finixWebhookStatus.effective_env_enabled && 
                   finixWebhookStatus.app_webhooks_enabled && 
                   finixWebhookStatus.webhook_secret_present ? (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      <CheckCircle size={14} className="mr-1" />
                      Ready
                    </Badge>
                  ) : finixWebhookStatus.effective_env_enabled ? (
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                      <Warning size={14} className="mr-1" />
                      Setup Required
                    </Badge>
                  ) : (
                    <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30">
                      Disabled
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Deployment Kill Switch Section */}
                <div className="p-4 bg-zinc-900 rounded-lg space-y-3">
                  <p className="text-sm font-medium text-zinc-300">Deployment Kill Switch</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="flex items-center justify-between p-2 bg-zinc-800 rounded">
                      <span className="text-xs text-zinc-400">ENV Kill Switch</span>
                      <Badge className={finixWebhookStatus.env_kill_switch_enabled 
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs" 
                        : "bg-zinc-500/20 text-zinc-400 border-zinc-500/30 text-xs"}>
                        {finixWebhookStatus.env_kill_switch_enabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-zinc-800 rounded">
                      <span className="text-xs text-zinc-400">UI Override</span>
                      <Badge className={finixWebhookStatus.db_env_override_enabled 
                        ? "bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs" 
                        : "bg-zinc-500/20 text-zinc-400 border-zinc-500/30 text-xs"}>
                        {finixWebhookStatus.db_env_override_enabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-zinc-800 rounded">
                      <span className="text-xs text-zinc-400">Effective Status</span>
                      <Badge className={finixWebhookStatus.effective_env_enabled 
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs" 
                        : "bg-red-500/20 text-red-400 border-red-500/30 text-xs"}>
                        {finixWebhookStatus.effective_env_enabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                  </div>
                  {/* Override Toggle */}
                  <div className="flex items-center justify-between pt-2 border-t border-zinc-700">
                    <div>
                      <Label className="text-zinc-300 text-sm">Override Deployment Kill Switch (Preview Mode)</Label>
                      <p className="text-xs text-zinc-500">Enable webhooks via UI when ENV cannot be changed</p>
                    </div>
                    <Switch
                      checked={finixWebhookStatus.db_env_override_enabled}
                      onCheckedChange={async (checked) => {
                        setSavingWebhook(true);
                        try {
                          const res = await fetch(`${API}/admin/system/finix/webhook-settings`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            credentials: "include",
                            body: JSON.stringify({ webhook_env_override_enabled: checked }),
                          });
                          const data = await res.json();
                          if (res.ok) {
                            setFinixWebhookStatus(prev => ({ ...prev, ...data }));
                            toast.success(checked ? "UI override enabled" : "UI override disabled");
                          } else {
                            const msg = data.detail?.message || data.detail || "Failed to update";
                            toast.error(msg);
                          }
                        } catch (err) {
                          toast.error("Failed to update webhook settings");
                        } finally {
                          setSavingWebhook(false);
                        }
                      }}
                      data-testid="finix-webhook-override-toggle"
                    />
                  </div>
                </div>

                {/* Status and Controls */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
                      <div>
                        <p className="text-sm text-zinc-300">App Webhook Toggle (DB)</p>
                        <p className="text-xs text-zinc-500">Configurable via UI</p>
                      </div>
                      <Badge className={finixWebhookStatus.app_webhooks_enabled 
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" 
                        : "bg-zinc-500/20 text-zinc-400 border-zinc-500/30"}>
                        {finixWebhookStatus.app_webhooks_enabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
                      <div>
                        <p className="text-sm text-zinc-300">Webhook Secret</p>
                        <p className="text-xs text-zinc-500">HMAC signature verification</p>
                      </div>
                      <Badge className={finixWebhookStatus.webhook_secret_present 
                        ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" 
                        : "bg-amber-500/20 text-amber-400 border-amber-500/30"}>
                        {finixWebhookStatus.webhook_secret_present ? "Present" : "Not Set"}
                      </Badge>
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="space-y-4">
                    {/* Enable Toggle */}
                    <div className="flex items-center justify-between p-3 bg-zinc-900 rounded-lg">
                      <div>
                        <Label className="text-zinc-300">Enable Webhook Receiver</Label>
                        <p className="text-xs text-zinc-500">
                          {!finixWebhookStatus.effective_env_enabled 
                            ? "Requires effective ENV enabled first" 
                            : "Process incoming Finix webhooks"}
                        </p>
                      </div>
                      <Switch
                        checked={finixWebhookStatus.app_webhooks_enabled}
                        disabled={!finixWebhookStatus.effective_env_enabled}
                        onCheckedChange={async (checked) => {
                          // If enabling without secret, show error
                          if (checked && !finixWebhookStatus.webhook_secret_present && !finixWebhookSecret) {
                            toast.error("Cannot enable: webhook secret required");
                            return;
                          }
                          setSavingWebhook(true);
                          try {
                            const payload = { app_webhooks_enabled: checked };
                            if (finixWebhookSecret) {
                              payload.webhook_secret = finixWebhookSecret;
                            }
                            const res = await fetch(`${API}/admin/system/finix/webhook-settings`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              credentials: "include",
                              body: JSON.stringify(payload),
                            });
                            const data = await res.json();
                            if (res.ok) {
                              setFinixWebhookStatus(prev => ({ ...prev, ...data }));
                              setFinixWebhookSecret("");
                              toast.success(checked ? "Webhook receiver enabled" : "Webhook receiver disabled");
                            } else {
                              const msg = data.detail?.message || data.detail || "Failed to update";
                              toast.error(msg);
                            }
                          } catch (err) {
                            toast.error("Failed to update webhook settings");
                          } finally {
                            setSavingWebhook(false);
                          }
                        }}
                        data-testid="finix-webhook-toggle"
                      />
                    </div>

                    {/* Secret Input */}
                    <div>
                      <Label className="text-zinc-300">
                        Webhook Secret
                        <span className="ml-2 text-xs text-zinc-500">
                          {finixWebhookStatus.webhook_secret_present ? "(Leave blank to keep existing)" : "(Required)"}
                        </span>
                      </Label>
                      <div className="relative mt-1">
                        <Input
                          type={showWebhookSecret ? "text" : "password"}
                          value={finixWebhookSecret}
                          onChange={(e) => setFinixWebhookSecret(e.target.value)}
                          placeholder={finixWebhookStatus.webhook_secret_present ? "••••••••••••" : "Enter webhook secret from Finix"}
                          className="bg-zinc-900 border-zinc-600 text-white pr-10"
                          data-testid="finix-webhook-secret-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400"
                        >
                          {showWebhookSecret ? <EyeSlash size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>

                    {/* Webhook URL (read-only) */}
                    <div>
                      <Label className="text-zinc-300">Webhook URL</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="text"
                          value={finixWebhookStatus.webhook_url}
                          readOnly
                          className="bg-zinc-900 border-zinc-600 text-zinc-400 font-mono text-sm"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            navigator.clipboard.writeText(finixWebhookStatus.webhook_url);
                            toast.success("URL copied to clipboard");
                          }}
                          className="border-zinc-600 text-zinc-400 hover:bg-zinc-700 flex-shrink-0"
                        >
                          <Copy size={16} />
                        </Button>
                      </div>
                    </div>

                    {/* Save Button */}
                    <Button
                      onClick={async () => {
                        if (!finixWebhookSecret) {
                          toast.error("Enter a webhook secret to save");
                          return;
                        }
                        setSavingWebhook(true);
                        try {
                          const res = await fetch(`${API}/admin/system/finix/webhook-settings`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            credentials: "include",
                            body: JSON.stringify({ webhook_secret: finixWebhookSecret }),
                          });
                          const data = await res.json();
                          if (res.ok) {
                            setFinixWebhookStatus(prev => ({ ...prev, ...data }));
                            setFinixWebhookSecret("");
                            toast.success("Webhook secret saved");
                          } else {
                            const msg = data.detail?.message || data.detail || "Failed to save";
                            toast.error(msg);
                          }
                        } catch (err) {
                          toast.error("Failed to save webhook settings");
                        } finally {
                          setSavingWebhook(false);
                        }
                      }}
                      disabled={savingWebhook || !finixWebhookSecret}
                      className="w-full bg-[#FF6600] hover:bg-[#CC5200] text-white"
                      data-testid="finix-webhook-save-btn"
                    >
                      {savingWebhook ? "Saving..." : "Save Webhook Secret"}
                    </Button>
                  </div>
                </div>

                {/* Last Event Info */}
                {finixWebhookStatus.last_event && (
                  <div className="p-3 bg-zinc-900 rounded-lg">
                    <p className="text-sm text-zinc-300 mb-1">Last Webhook Received</p>
                    <div className="flex gap-4 text-xs text-zinc-400 font-mono">
                      <span>Type: {finixWebhookStatus.last_event.event_type || "unknown"}</span>
                      <span>ID: {finixWebhookStatus.last_event.event_id?.slice(0, 12) || "N/A"}...</span>
                      <span>At: {new Date(finixWebhookStatus.last_event.received_at).toLocaleString()}</span>
                    </div>
                  </div>
                )}

                {/* Help Text */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex gap-3">
                    <Info size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-200">
                      <p className="font-medium mb-1">How to configure in Finix Dashboard</p>
                      <p className="text-blue-300/80">
                        Go to <strong>Developer → Webhooks</strong> in Finix Dashboard. Create a new webhook with the URL above 
                        and enter the same secret. After saving, check <strong>Deliveries</strong> to verify HTTP 200/204 responses.
                      </p>
                      <p className="text-blue-300/60 mt-2 text-xs">
                        Disputes and refunds performed in Finix Dashboard are reflected in FSP via webhooks when enabled.
                      </p>
                      <p className="text-blue-300/60 mt-1 text-xs">
                        <strong>UI Override:</strong> Use the override toggle if the deployment ENV variable cannot be changed (e.g., preview/testing environments).
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tokenization Test Card - Only shown when Finix is configured */}
            {finixSettings.configured && finixSettings.application_id && (
              <Card className="bg-zinc-800 border-zinc-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white flex items-center gap-2">
                        <CreditCard size={20} />
                        Tokenization Test
                      </CardTitle>
                      <CardDescription className="text-zinc-400">
                        Test Finix Flex hosted fields tokenization (sandbox only)
                      </CardDescription>
                    </div>
                    <Badge className={finixSettings.environment === "sandbox" ? "bg-amber-500/20 text-amber-400 border-amber-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}>
                      {finixSettings.environment === "sandbox" ? "Sandbox" : "⚠️ Live"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Test Card Numbers Info */}
                  {finixSettings.environment === "sandbox" && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <div className="flex gap-3">
                        <Info size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-200">
                          <p className="font-medium mb-2">Sandbox Test Cards</p>
                          <div className="grid grid-cols-2 gap-2 text-blue-300/80 font-mono text-xs">
                            <div>Visa: 4111 1111 1111 1111</div>
                            <div>MC: 5555 5555 5555 4444</div>
                            <div>Amex: 3782 8224 6310 005</div>
                            <div>Exp: Any future date, CVV: Any 3-4 digits</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Toggle Test Form */}
                  {!showTokenizationTest ? (
                    <Button
                      onClick={() => {
                        setShowTokenizationTest(true);
                        setFinixTestToken(null);
                        setFinixTestError(null);
                      }}
                      variant="outline"
                      className="w-full border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                      data-testid="finix-show-test-form-btn"
                    >
                      <CreditCard size={16} className="mr-2" />
                      Open Tokenization Test Form
                    </Button>
                  ) : (
                    <div className="space-y-4">
                      {/* Finix Hosted Fields Form */}
                      <div className="bg-zinc-900 rounded-lg p-4 border border-zinc-700">
                        <FinixTokenizationForm
                          applicationId={finixSettings.application_id}
                          environment={finixSettings.environment}
                          onTokenize={(result) => {
                            console.log('[AdminFinixTest] Tokenization success');
                            setFinixTestToken({
                              tokenId: result.tokenId ? `${result.tokenId.slice(0, 8)}...${result.tokenId.slice(-4)}` : null,
                              fullTokenId: result.tokenId, // Keep full for display (memory-only)
                              cardBrand: result.cardBrand,
                              lastFour: result.lastFour,
                              timestamp: new Date().toISOString(),
                            });
                            setFinixTestError(null);
                            setFinixTestLoading(false);
                            toast.success('Tokenization successful!');
                          }}
                          onError={(error) => {
                            console.error('[AdminFinixTest] Tokenization error:', error);
                            setFinixTestError(error.message || 'Tokenization failed');
                            setFinixTestToken(null);
                            setFinixTestLoading(false);
                            toast.error('Tokenization failed');
                          }}
                          onReady={() => {
                            console.log('[AdminFinixTest] Form ready');
                          }}
                        />
                      </div>

                      {/* Test Result Display */}
                      {finixTestToken && (
                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4" data-testid="finix-test-token-result">
                          <div className="flex items-start gap-3">
                            <CheckCircle size={20} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                            <div className="text-sm">
                              <p className="font-medium text-emerald-300">Token Created Successfully</p>
                              <div className="mt-2 space-y-1 text-emerald-200/80 font-mono text-xs">
                                <p>Token ID: {finixTestToken.tokenId}</p>
                                {finixTestToken.cardBrand && <p>Brand: {finixTestToken.cardBrand}</p>}
                                {finixTestToken.lastFour && <p>Last 4: ****{finixTestToken.lastFour}</p>}
                                <p className="text-emerald-400/60">Created: {finixTestToken.timestamp}</p>
                              </div>
                              <p className="mt-3 text-xs text-emerald-300/60 italic">
                                ⓘ Token is in memory only. It will be cleared on page refresh.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {finixTestError && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4" data-testid="finix-test-token-error">
                          <div className="flex items-start gap-3">
                            <Warning size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                            <div className="text-sm">
                              <p className="font-medium text-red-300">Tokenization Failed</p>
                              <p className="mt-1 text-red-200/80">{finixTestError}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Close Test Form */}
                      <div className="flex justify-end gap-2">
                        {finixTestToken && (
                          <Button
                            onClick={() => {
                              setFinixTestToken(null);
                              toast.info('Token cleared from memory');
                            }}
                            variant="outline"
                            size="sm"
                            className="border-zinc-600 text-zinc-400 hover:bg-zinc-700"
                          >
                            Clear Token
                          </Button>
                        )}
                        <Button
                          onClick={() => {
                            setShowTokenizationTest(false);
                            setFinixTestToken(null);
                            setFinixTestError(null);
                          }}
                          variant="outline"
                          size="sm"
                          className="border-zinc-600 text-zinc-400 hover:bg-zinc-700"
                          data-testid="finix-close-test-form-btn"
                        >
                          Close Test Form
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Show message when not configured */}
            {!finixSettings.configured && (
              <Card className="bg-zinc-800/50 border-zinc-700/50">
                <CardContent className="py-8 text-center text-zinc-500">
                  <CreditCard size={32} className="mx-auto mb-3 opacity-50" />
                  <p>Complete Finix configuration above to enable tokenization testing.</p>
                  <p className="text-sm mt-1">Required: Enabled + Merchant ID + Username + Password</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Twilio Settings */}
        <TabsContent value="twilio">
          <div className="space-y-6">
            <Card className="bg-zinc-800 border-zinc-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <ChatCircleText size={20} />
                      Twilio SMS Configuration
                    </CardTitle>
                    <CardDescription className="text-zinc-400">
                      Configure SMS notifications and reminders
                    </CardDescription>
                  </div>
                  {twilioSettings.account_sid_configured ? (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      <CheckCircle size={14} className="mr-1" />
                      Connected
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                      <Warning size={14} className="mr-1" />
                      Not Configured
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-zinc-300">Account SID</Label>
                  <Input
                    type="text"
                    value={twilioSettings.account_sid}
                    onChange={(e) => setTwilioSettings({ ...twilioSettings, account_sid: e.target.value })}
                    placeholder={twilioSettings.account_sid_masked || "ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"}
                    className="mt-1 bg-zinc-900 border-zinc-600 text-white"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Found in your Twilio Console dashboard</p>
                </div>

                <div>
                  <Label className="text-zinc-300">Auth Token</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showTwilioToken ? "text" : "password"}
                      value={twilioSettings.auth_token}
                      onChange={(e) => setTwilioSettings({ ...twilioSettings, auth_token: e.target.value })}
                      placeholder={twilioSettings.auth_token_configured ? "••••••••••••••••" : "Enter Auth Token"}
                      className="bg-zinc-900 border-zinc-600 text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowTwilioToken(!showTwilioToken)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400"
                    >
                      {showTwilioToken ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-zinc-300">Twilio Phone Number</Label>
                  <div className="relative mt-1">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <Input
                      type="tel"
                      value={twilioSettings.phone_number}
                      onChange={(e) => setTwilioSettings({ ...twilioSettings, phone_number: e.target.value })}
                      placeholder="+1234567890"
                      className="bg-zinc-900 border-zinc-600 text-white pl-10"
                    />
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">Your Twilio phone number in E.164 format</p>
                </div>

                <div>
                  <Label className="text-zinc-300">Messaging Service SID (Optional)</Label>
                  <Input
                    type="text"
                    value={twilioSettings.messaging_service_sid}
                    onChange={(e) => setTwilioSettings({ ...twilioSettings, messaging_service_sid: e.target.value })}
                    placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="mt-1 bg-zinc-900 border-zinc-600 text-white"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Use for higher volume or A2P messaging</p>
                </div>

                <div>
                  <Label className="text-zinc-300">Mode</Label>
                  <Select value={twilioSettings.mode} onValueChange={(value) => setTwilioSettings({ ...twilioSettings, mode: value })}>
                    <SelectTrigger className="mt-1 bg-zinc-900 border-zinc-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="test">Test (Validate only, no SMS sent)</SelectItem>
                      <SelectItem value="live">Live (Send real SMS messages)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="pt-4 border-t border-zinc-700">
                  <p className="text-xs text-zinc-500 mb-4">
                    Get your credentials from{" "}
                    <a href="https://console.twilio.com/" target="_blank" rel="noopener noreferrer" className="text-[#FF6600] hover:underline">
                      Twilio Console
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-800 border-zinc-700">
              <CardHeader>
                <CardTitle className="text-white text-base">Test SMS</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-zinc-300">Test Phone Number</Label>
                  <div className="relative mt-1">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <Input
                      type="tel"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                      placeholder="+1234567890"
                      className="bg-zinc-900 border-zinc-600 text-white pl-10"
                    />
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">Phone number to receive test SMS</p>
                </div>
                <div className="flex gap-3">
                  <Button onClick={saveTwilioSettings} disabled={saving} className="bg-[#FF6600] hover:bg-[#CC5200] text-white">
                    {saving ? "Saving..." : "Save Settings"}
                  </Button>
                  <Button variant="outline" onClick={testTwilio} disabled={testing || !twilioSettings.account_sid_configured} className="border-zinc-600 text-zinc-300 hover:bg-zinc-700">
                    {testing ? <><ArrowClockwise size={16} className="mr-2 animate-spin" />Testing...</> : <><ChatCircleText size={16} className="mr-2" />Send Test SMS</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Translation Settings */}
        <TabsContent value="translation">
          <div className="space-y-6">
            <Card className="bg-zinc-800 border-zinc-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Translate size={20} />
                      Spanish Translation (AI-Powered)
                    </CardTitle>
                    <CardDescription className="text-zinc-400">
                      Translate content for Spanish-speaking technicians and customers
                    </CardDescription>
                  </div>
                  {translationSettings.api_key_configured ? (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      <CheckCircle size={14} className="mr-1" />
                      API Key Ready
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                      <Warning size={14} className="mr-1" />
                      Not Configured
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Entitlement-Based Info Box */}
                <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
                  <p className="text-sm text-indigo-300 font-medium mb-2">
                    Translation is Entitlement-Based
                  </p>
                  <p className="text-xs text-zinc-400">
                    Spanish translation is automatically enabled for tenants with the <strong className="text-indigo-300">spanish_translation</strong> entitlement. 
                    Only technicians with <strong className="text-indigo-300">preferred_language = "es"</strong> will see translated content.
                    No global toggle needed.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">AI Provider</Label>
                    <Select value={translationSettings.provider} onValueChange={(value) => setTranslationSettings({ ...translationSettings, provider: value })}>
                      <SelectTrigger className="mt-1 bg-zinc-900 border-zinc-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="openai">OpenAI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Model</Label>
                    <Select value={translationSettings.model} onValueChange={(value) => setTranslationSettings({ ...translationSettings, model: value })}>
                      <SelectTrigger className="mt-1 bg-zinc-900 border-zinc-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gpt-4.1-mini">GPT-4.1 Mini (Fast & Affordable)</SelectItem>
                        <SelectItem value="gpt-4.1">GPT-4.1 (High Quality)</SelectItem>
                        <SelectItem value="gpt-5.2">GPT-5.2 (Latest)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-zinc-300">Target Language</Label>
                  <Select value={translationSettings.target_language} onValueChange={(value) => setTranslationSettings({ ...translationSettings, target_language: value })}>
                    <SelectTrigger className="mt-1 bg-zinc-900 border-zinc-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="es">Spanish (Español)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500 mt-1">Language to translate content into</p>
                </div>

                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-300">
                    <strong>Powered by Emergent LLM Key</strong> — Translation uses your platform&apos;s universal API key. 
                    Credits are deducted from your key balance per translation request.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-800 border-zinc-700">
              <CardHeader>
                <CardTitle className="text-white text-base">Test Translation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-zinc-300">Text to Translate</Label>
                  <Input
                    value={testText}
                    onChange={(e) => setTestText(e.target.value)}
                    placeholder="Enter text to translate..."
                    className="mt-1 bg-zinc-900 border-zinc-600 text-white"
                  />
                </div>

                {translationResult && (
                  <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg space-y-2">
                    <p className="text-xs text-emerald-400 uppercase tracking-wide">Translation Result</p>
                    <p className="text-zinc-300"><strong>Original:</strong> {translationResult.original}</p>
                    <p className="text-emerald-300"><strong>Spanish:</strong> {translationResult.translated}</p>
                    <p className="text-xs text-zinc-500">Model: {translationResult.model}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button onClick={saveTranslationSettings} disabled={saving} className="bg-[#FF6600] hover:bg-[#CC5200] text-white">
                    {saving ? "Saving..." : "Save Settings"}
                  </Button>
                  <Button variant="outline" onClick={testTranslation} disabled={testing || !translationSettings.api_key_configured || !testText.trim()} className="border-zinc-600 text-zinc-300 hover:bg-zinc-700">
                    {testing ? <><ArrowClockwise size={16} className="mr-2 animate-spin" />Translating...</> : <><Translate size={16} className="mr-2" />Test Translation</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Comms Ledger Panel */}
        <TabsContent value="comms-ledger">
          <CommsLedgerPanel />
        </TabsContent>

        {/* Payment Logs */}
        <TabsContent value="payment-logs">
          <div className="space-y-6">
            {/* Payment Stats */}
            <div className="grid grid-cols-5 gap-4">
              {[
                { key: "total", label: "Total Events", color: "text-zinc-300" },
                { key: "success", label: "Successful", color: "text-emerald-400" },
                { key: "failed", label: "Failed", color: "text-red-400" },
                { key: "charges", label: "Charges", color: "text-blue-400" },
                { key: "tokens", label: "Tokens", color: "text-purple-400" },
              ].map(({ key, label, color }) => (
                <Card key={key} className="bg-zinc-800 border-zinc-700">
                  <CardContent className="p-4 text-center">
                    <p className={`text-2xl font-bold ${color}`}>{paymentStats[key] || 0}</p>
                    <p className="text-xs text-zinc-500">{label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="bg-zinc-800 border-zinc-700">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <CreditCard size={20} />
                      Payment Processing Logs
                    </CardTitle>
                    <CardDescription className="text-zinc-400">Transaction events and payment gateway activity</CardDescription>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select 
                      value={paymentLogFilter.event_type} 
                      onValueChange={(v) => setPaymentLogFilter({...paymentLogFilter, event_type: v})}
                    >
                      <SelectTrigger className="w-[160px] bg-zinc-900 border-zinc-600 text-zinc-300">
                        <SelectValue placeholder="Event Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Events</SelectItem>
                        <SelectItem value="charge_stored_card">Charges</SelectItem>
                        <SelectItem value="token_registered">Card Stored</SelectItem>
                        <SelectItem value="refund">Refunds</SelectItem>
                        <SelectItem value="void">Voids</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select 
                      value={paymentLogFilter.result} 
                      onValueChange={(v) => setPaymentLogFilter({...paymentLogFilter, result: v})}
                    >
                      <SelectTrigger className="w-[120px] bg-zinc-900 border-zinc-600 text-zinc-300">
                        <SelectValue placeholder="Result" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Results</SelectItem>
                        <SelectItem value="success">Success</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" onClick={fetchPaymentLogs} className="text-zinc-400 hover:text-white">
                      <ArrowClockwise size={16} className="mr-2" />
                      Refresh
                    </Button>
                  </div>
                </div>
                {/* Stats row */}
                <div className="flex flex-wrap gap-4 mt-4 text-sm">
                  <span className="text-zinc-400">Total: <span className="text-white font-medium">{paymentStats.total || 0}</span></span>
                  <span className="text-zinc-400">Charges: <span className="text-emerald-400 font-medium">{paymentStats.charges || 0}</span></span>
                  <span className="text-zinc-400">Refunds: <span className="text-orange-400 font-medium">{paymentStats.refunds || 0}</span></span>
                  <span className="text-zinc-400">Voids: <span className="text-red-400 font-medium">{paymentStats.voids || 0}</span></span>
                  <span className="text-zinc-400">Failed: <span className="text-red-400 font-medium">{paymentStats.failed || 0}</span></span>
                </div>
              </CardHeader>
              <CardContent>
                {paymentLogs.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No payment logs yet</p>
                ) : (
                  <div className="space-y-2">
                    {/* Table header for larger screens */}
                    <div className="hidden md:grid md:grid-cols-[1fr_1fr_100px_120px_100px_80px] gap-2 px-3 py-2 bg-zinc-900/50 rounded text-zinc-400 text-xs font-medium uppercase">
                      <div>Event</div>
                      <div>Tenant</div>
                      <div className="text-right">Amount</div>
                      <div>Result</div>
                      <div>Date</div>
                      <div className="text-right">Actions</div>
                    </div>
                    
                    {/* Log entries */}
                    {paymentLogs.map((log, i) => (
                      <div 
                        key={log.log_id || i} 
                        className="bg-zinc-900/30 rounded p-3 hover:bg-zinc-900/50 transition-colors"
                      >
                        {/* Mobile layout (stacked) */}
                        <div className="md:hidden space-y-2">
                          <div className="flex items-center justify-between">
                            <Badge className={`${
                              log.event_type === "refund" ? "bg-orange-500/20 text-orange-400" :
                              log.event_type === "void" ? "bg-red-500/20 text-red-400" :
                              log.event_type?.includes("charge") ? "bg-emerald-500/20 text-emerald-400" :
                              log.event_type?.includes("token") ? "bg-purple-500/20 text-purple-400" :
                              "bg-zinc-500/20 text-zinc-400"
                            }`}>
                              {log.event_type?.replace(/_/g, " ")}
                            </Badge>
                            <Badge className={log.result === "success" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}>
                              {log.result}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-zinc-400 truncate max-w-[150px]" title={log.tenant_name}>
                              {log.tenant_name || log.tenant_id?.slice(-8) || "-"}
                            </span>
                            <span className="text-emerald-400 font-medium">
                              {formatAmount(log.amount_cents || log.request_summary?.amount_cents)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-zinc-500">
                            <span>{formatDate(log.timestamp)}</span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 px-2 text-zinc-400 hover:text-white"
                              onClick={() => openDetailsModal(log)}
                            >
                              <Eye size={14} className="mr-1" /> View
                            </Button>
                          </div>
                        </div>
                        
                        {/* Desktop layout (grid) */}
                        <div className="hidden md:grid md:grid-cols-[1fr_1fr_100px_120px_100px_80px] gap-2 items-center">
                          <div>
                            <Badge className={`${
                              log.event_type === "refund" ? "bg-orange-500/20 text-orange-400" :
                              log.event_type === "void" ? "bg-red-500/20 text-red-400" :
                              log.event_type?.includes("charge") ? "bg-emerald-500/20 text-emerald-400" :
                              log.event_type?.includes("token") ? "bg-purple-500/20 text-purple-400" :
                              "bg-zinc-500/20 text-zinc-400"
                            }`}>
                              {log.event_type?.replace(/_/g, " ")}
                            </Badge>
                          </div>
                          <div className="text-zinc-300 text-sm truncate" title={log.tenant_name}>
                            {log.tenant_name || log.tenant_id?.slice(-8) || "-"}
                          </div>
                          <div className="text-right text-emerald-400 font-medium text-sm">
                            {formatAmount(log.amount_cents || log.request_summary?.amount_cents)}
                          </div>
                          <div>
                            <Badge className={log.result === "success" ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"}>
                              {log.result}
                            </Badge>
                          </div>
                          <div className="text-zinc-400 text-xs">{formatDate(log.timestamp)}</div>
                          <div className="text-right">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-7 px-2 text-zinc-400 hover:text-white"
                              onClick={() => openDetailsModal(log)}
                            >
                              <Eye size={14} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Transaction Details Modal */}
            <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
              <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center gap-2">
                    <CreditCard size={20} />
                    Transaction Details
                  </DialogTitle>
                </DialogHeader>
                {selectedTransaction && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <Label className="text-zinc-400">Event Type</Label>
                        <p className="text-white">{selectedTransaction.event_type?.replace(/_/g, " ")}</p>
                      </div>
                      <div>
                        <Label className="text-zinc-400">Result</Label>
                        <Badge className={
                          selectedTransaction.result === "success" ? "bg-emerald-500/20 text-emerald-400" : 
                          selectedTransaction.status === "voided" ? "bg-zinc-500/20 text-zinc-400" :
                          "bg-red-500/20 text-red-400"
                        }>
                          {selectedTransaction.status === "voided" ? "voided" : selectedTransaction.result}
                        </Badge>
                      </div>
                      <div>
                        <Label className="text-zinc-400">Tenant</Label>
                        <p className="text-white">{selectedTransaction.tenant_name || selectedTransaction.tenant_id || "-"}</p>
                      </div>
                      <div>
                        <Label className="text-zinc-400">Amount</Label>
                        <p className={selectedTransaction.status === "voided" ? "text-zinc-500 line-through font-medium" : "text-emerald-400 font-medium"}>
                          {formatAmount(selectedTransaction.amount_cents || selectedTransaction.request_summary?.amount_cents)}
                        </p>
                      </div>
                      <div>
                        <Label className="text-zinc-400">Date/Time</Label>
                        <p className="text-white">{formatDate(selectedTransaction.timestamp)}</p>
                      </div>
                      <div>
                        {/* Show Finix Charge ID for Finix records, Gateway Trans ID for TrustCommerce */}
                        {selectedTransaction.processor === "finix" ? (
                          <>
                            <Label className="text-zinc-400">Finix Charge ID</Label>
                            <p className="text-zinc-300 font-mono text-xs">
                              {selectedTransaction.finix_charge_id || 
                               selectedTransaction.response_summary?.finix_charge_id || 
                               <span className="text-zinc-500">Not available</span>}
                            </p>
                          </>
                        ) : (
                          <>
                            <Label className="text-zinc-400">Gateway Trans ID</Label>
                            <p className="text-zinc-300 font-mono text-xs" title={selectedTransaction.has_tc_transid === false ? "Not stored for legacy transactions" : "Masked for security"}>
                              {selectedTransaction.tc_transid_masked || 
                               (selectedTransaction.tc_transid ? `...${selectedTransaction.tc_transid.slice(-6)}` : 
                               selectedTransaction.response_summary?.transid ? `...${selectedTransaction.response_summary.transid.slice(-6)}` : 
                               <span className="text-amber-400">Not stored</span>)}
                            </p>
                          </>
                        )}
                      </div>
                      {/* Show Finix Merchant ID for Finix records */}
                      {selectedTransaction.processor === "finix" && selectedTransaction.finix_merchant_id && (
                        <div>
                          <Label className="text-zinc-400">Merchant ID</Label>
                          <p className="text-zinc-300 font-mono text-xs">
                            {selectedTransaction.finix_merchant_id}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Voided transaction notice */}
                    {selectedTransaction.status === "voided" && (
                      <div className="bg-zinc-800 border border-zinc-700 rounded p-3">
                        <div className="flex items-center gap-2 text-zinc-400">
                          <X size={16} />
                          <span className="font-medium">Transaction Voided</span>
                        </div>
                        <p className="text-zinc-500 text-sm mt-1">
                          This transaction was voided and will not appear on the customer&apos;s statement.
                        </p>
                      </div>
                    )}
                    
                    {/* Error message if failed */}
                    {selectedTransaction.result === "failed" && selectedTransaction.status !== "voided" && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                        <Label className="text-red-400">Error</Label>
                        <p className="text-red-300 text-sm">
                          {selectedTransaction.response_summary?.error || 
                           selectedTransaction.tc_error || 
                           "Transaction declined"}
                        </p>
                      </div>
                    )}
                    
                    {/* Finix records: Show read-only notice instead of actions */}
                    {selectedTransaction.processor === "finix" && selectedTransaction.status !== "voided" && (
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3">
                        <p className="text-xs text-blue-400">
                          This is a Finix transaction. Refunds and voids are managed through the Finix Dashboard.
                        </p>
                      </div>
                    )}
                    
                    {/* Refund/Void eligibility info - only show for NON-Finix, non-voided transactions */}
                    {transactionEligibility && selectedTransaction.status !== "voided" && selectedTransaction.processor !== "finix" && (
                      <div className="bg-zinc-800 rounded p-3 space-y-2">
                        <Label className="text-zinc-400">Actions Available</Label>
                        {transactionEligibility.has_tc_transid === false ? (
                          <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2">
                            <p className="text-xs text-amber-400 flex items-center gap-1">
                              <Warning size={14} />
                              Refund/Void unavailable: gateway transaction ID not stored for this legacy transaction.
                            </p>
                          </div>
                        ) : (
                          <>
                            <p className="text-xs text-zinc-500">
                              Charge: {transactionEligibility.charge_date_eastern} | Now: {transactionEligibility.current_time_eastern}
                            </p>
                            {transactionEligibility.reason && (
                              <p className="text-xs text-zinc-400">{transactionEligibility.reason}</p>
                            )}
                            <div className="flex gap-2 mt-3">
                              {transactionEligibility.can_refund && (
                                <Button 
                                  className="bg-orange-600 hover:bg-orange-700 text-white"
                                  onClick={() => {
                                    setRefundAmount("");
                                    setShowRefundModal(true);
                                  }}
                                >
                                  <ArrowCounterClockwise size={16} className="mr-2" />
                                  Refund
                                </Button>
                              )}
                              {transactionEligibility.can_void && (
                                <Button 
                                  className="bg-red-600 hover:bg-red-700 text-white"
                                  onClick={() => setShowVoidModal(true)}
                                >
                                  <X size={16} className="mr-2" />
                                  Void
                                </Button>
                              )}
                              {!transactionEligibility.can_refund && !transactionEligibility.can_void && (
                                <p className="text-zinc-500 text-sm">No actions available at this time</p>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    
                    {/* Show "No actions" message for voided transactions instead of broken controls */}
                    {selectedTransaction.status === "voided" && (
                      <div className="bg-zinc-800 rounded p-3">
                        <Label className="text-zinc-400">Actions</Label>
                        <p className="text-zinc-500 text-sm mt-1">No actions available for voided transactions.</p>
                      </div>
                    )}
                  </div>
                )}
                <DialogFooter>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowDetailsModal(false)} 
                    className="border-zinc-600 text-zinc-300 hover:text-white hover:bg-zinc-800"
                  >
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Refund Confirmation Modal */}
            <Dialog open={showRefundModal} onOpenChange={setShowRefundModal}>
              <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center gap-2">
                    <ArrowCounterClockwise size={20} className="text-orange-400" />
                    Process Refund
                  </DialogTitle>
                  <DialogDescription className="text-zinc-400">
                    This will credit funds back to the customer using TrustCommerce.
                  </DialogDescription>
                </DialogHeader>
                {transactionEligibility && (
                  <div className="space-y-4">
                    <div className="bg-zinc-800 rounded p-3 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Original Amount:</span>
                        <span className="text-white">{formatAmount(transactionEligibility.original_amount_cents)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Already Refunded:</span>
                        <span className="text-orange-400">{formatAmount(transactionEligibility.refunded_amount_cents)}</span>
                      </div>
                      <div className="flex justify-between border-t border-zinc-700 pt-2">
                        <span className="text-zinc-400">Refundable:</span>
                        <span className="text-emerald-400 font-medium">{formatAmount(transactionEligibility.refundable_amount_cents)}</span>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-zinc-400">Refund Amount (leave empty for full refund)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-zinc-400">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          max={(transactionEligibility.refundable_amount_cents / 100).toFixed(2)}
                          placeholder={`Max: ${(transactionEligibility.refundable_amount_cents / 100).toFixed(2)}`}
                          value={refundAmount}
                          onChange={(e) => setRefundAmount(e.target.value)}
                          className="bg-zinc-800 border-zinc-600 text-white"
                        />
                      </div>
                    </div>
                  </div>
                )}
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setShowRefundModal(false)} className="border-zinc-600 text-zinc-300 hover:text-white hover:bg-zinc-800">
                    Cancel
                  </Button>
                  <Button 
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={processRefund}
                    disabled={processingAction}
                  >
                    {processingAction ? "Processing..." : "Confirm Refund"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Void Confirmation Modal */}
            <Dialog open={showVoidModal} onOpenChange={setShowVoidModal}>
              <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center gap-2">
                    <X size={20} className="text-red-400" />
                    Void Transaction
                  </DialogTitle>
                  <DialogDescription className="text-zinc-400">
                    This will cancel the transaction before settlement. The charge will not appear on the customer&apos;s statement.
                  </DialogDescription>
                </DialogHeader>
                {transactionEligibility && (
                  <div className="bg-zinc-800 rounded p-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Amount to Void:</span>
                      <span className="text-white font-medium">{formatAmount(transactionEligibility.original_amount_cents)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Gateway Trans ID:</span>
                      <span className="text-zinc-300 font-mono text-xs">{transactionEligibility.tc_transid_masked || "..."}</span>
                    </div>
                  </div>
                )}
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setShowVoidModal(false)} className="border-zinc-600 text-zinc-300 hover:text-white hover:bg-zinc-800">
                    Cancel
                  </Button>
                  <Button 
                    className="bg-red-600 hover:bg-red-700 text-white"
                    onClick={processVoid}
                    disabled={processingAction}
                  >
                    {processingAction ? "Processing..." : "Confirm Void"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </TabsContent>

        {/* Webhook Events */}
        <TabsContent value="events">
          <div className="space-y-6">
            <div className="grid grid-cols-6 gap-4">
              {[
                { key: "delivered", label: "Delivered", color: "text-emerald-400" },
                { key: "open", label: "Opened", color: "text-blue-400" },
                { key: "click", label: "Clicked", color: "text-purple-400" },
                { key: "bounce", label: "Bounced", color: "text-red-400" },
                { key: "spamreport", label: "Spam", color: "text-orange-400" },
                { key: "dropped", label: "Dropped", color: "text-red-400" },
              ].map(({ key, label, color }) => (
                <Card key={key} className="bg-zinc-800 border-zinc-700">
                  <CardContent className="p-4 text-center">
                    <p className={`text-2xl font-bold ${color}`}>{eventStats[key] || 0}</p>
                    <p className="text-xs text-zinc-500">{label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="bg-zinc-800 border-zinc-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Webhook Events</CardTitle>
                    <CardDescription className="text-zinc-400">Real-time email delivery events</CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={fetchEmailEvents} className="text-zinc-400 hover:text-white">
                    <ArrowClockwise size={16} className="mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {emailEvents.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No webhook events received yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-zinc-700">
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Event</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Email</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailEvents.map((event, i) => (
                          <tr key={i} className="border-b border-zinc-700/50">
                            <td className="py-3 px-4">{getEventBadge(event.event_type)}</td>
                            <td className="py-3 px-4 text-zinc-300 font-mono text-sm">{event.email}</td>
                            <td className="py-3 px-4 text-zinc-400 text-sm">{formatDate(event.timestamp_dt || event.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* AdSense / Advertising Tab */}
        <TabsContent value="adsense">
          <div className="space-y-6">
            {/* Global Settings */}
            <Card className="bg-zinc-800 border-zinc-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Megaphone size={20} />
                      Advertising (Google AdSense)
                    </CardTitle>
                    <CardDescription className="text-zinc-400">
                      Configure ads for contractors without paid features. Changes take effect immediately.
                    </CardDescription>
                  </div>
                  <Button onClick={saveAdsenseSettings} disabled={saving}>
                    {saving ? "Saving..." : "Save Settings"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Critical Rule Info */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <div className="flex gap-3">
                    <Info size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-200">
                      <p className="font-medium mb-1">Ad Eligibility Rule (Hard Requirement)</p>
                      <p className="text-amber-300/80">
                        Ads will ONLY display for contractors with <strong>ZERO</strong> active feature subscriptions 
                        and <strong>ZERO</strong> active bundle subscriptions. Once a contractor purchases ANY feature 
                        or bundle, ads immediately stop rendering across all pages and devices.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Global Toggle */}
                <div className="flex items-center justify-between p-4 bg-zinc-900 rounded-lg">
                  <div>
                    <Label className="text-white font-medium">Ads Enabled (Global)</Label>
                    <p className="text-sm text-zinc-400 mt-1">Master switch to enable/disable all ads system-wide</p>
                  </div>
                  <Switch
                    checked={adsenseSettings.ads_enabled}
                    onCheckedChange={(checked) => setAdsenseSettings({...adsenseSettings, ads_enabled: checked})}
                  />
                </div>

                {/* Mode Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Mode</Label>
                    <Select 
                      value={adsenseSettings.ads_mode} 
                      onValueChange={(v) => setAdsenseSettings({...adsenseSettings, ads_mode: v})}
                    >
                      <SelectTrigger className="bg-zinc-900 border-zinc-600 text-zinc-300 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="test">Test Mode (No Revenue)</SelectItem>
                        <SelectItem value="live">Live Mode (Production)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-zinc-500 mt-1">
                      {adsenseSettings.ads_mode === "test" 
                        ? "Test ads shown, no revenue generated" 
                        : "Live ads shown, revenue generated"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Publisher ID</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type={showPublisherId ? "text" : "password"}
                        placeholder={adsenseSettings.publisher_id_configured ? adsenseSettings.publisher_id_masked : "ca-pub-XXXXXXXXXXXXXXXX"}
                        value={adsenseSettings.adsense_publisher_id}
                        onChange={(e) => setAdsenseSettings({...adsenseSettings, adsense_publisher_id: e.target.value})}
                        className="bg-zinc-900 border-zinc-600 text-zinc-300 font-mono"
                      />
                      <Button 
                        variant="outline" 
                        size="icon" 
                        onClick={() => setShowPublisherId(!showPublisherId)}
                        className="border-zinc-600"
                      >
                        {showPublisherId ? <EyeSlash size={16} /> : <Eye size={16} />}
                      </Button>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">
                      {adsenseSettings.publisher_id_configured 
                        ? `Configured: ${adsenseSettings.publisher_id_masked}` 
                        : "Not configured - enter your AdSense publisher ID"}
                    </p>
                  </div>
                </div>

                {/* Frequency Caps */}
                <div className="p-4 bg-zinc-900 rounded-lg space-y-4">
                  <Label className="text-white font-medium">Frequency Caps</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-400 text-sm">Max Impressions Per Session</Label>
                      <Input
                        type="number"
                        min="1"
                        max="20"
                        value={adsenseSettings.frequency_caps?.max_impressions_per_session || 5}
                        onChange={(e) => setAdsenseSettings({
                          ...adsenseSettings, 
                          frequency_caps: {
                            ...adsenseSettings.frequency_caps,
                            max_impressions_per_session: parseInt(e.target.value) || 5
                          }
                        })}
                        className="bg-zinc-800 border-zinc-600 text-zinc-300 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-sm">Max Impressions Per Day</Label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={adsenseSettings.frequency_caps?.max_impressions_per_day || 20}
                        onChange={(e) => setAdsenseSettings({
                          ...adsenseSettings, 
                          frequency_caps: {
                            ...adsenseSettings.frequency_caps,
                            max_impressions_per_day: parseInt(e.target.value) || 20
                          }
                        })}
                        className="bg-zinc-800 border-zinc-600 text-zinc-300 mt-1"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ad Units */}
            <Card className="bg-zinc-800 border-zinc-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Ad Units</CardTitle>
                    <CardDescription className="text-zinc-400">Configure ad slots for each placement</CardDescription>
                  </div>
                  <Button variant="outline" onClick={addAdUnit} className="border-zinc-600">
                    <Plus size={16} className="mr-2" />
                    Add Unit
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {adsenseSettings.ad_units?.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No ad units configured. Click &quot;Add Unit&quot; to create one.</p>
                ) : (
                  <div className="space-y-4">
                    {adsenseSettings.ad_units.map((unit, index) => (
                      <div key={index} className="p-4 bg-zinc-900 rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-white font-medium">Ad Unit {index + 1}</Label>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeAdUnit(index)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <Label className="text-zinc-400 text-xs">Name</Label>
                            <Input
                              placeholder="e.g., Dashboard Ad"
                              value={unit.name || ""}
                              onChange={(e) => updateAdUnit(index, "name", e.target.value)}
                              className="bg-zinc-800 border-zinc-600 text-zinc-300 mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-xs">Placement</Label>
                            <Select 
                              value={unit.placement_key || "dashboard_footer"}
                              onValueChange={(v) => updateAdUnit(index, "placement_key", v)}
                            >
                              <SelectTrigger className="bg-zinc-800 border-zinc-600 text-zinc-300 mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="dashboard_footer">Dashboard Footer</SelectItem>
                                <SelectItem value="listview_inline">List View Inline</SelectItem>
                                <SelectItem value="settings_footer">Settings Footer</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-xs">Ad Slot ID</Label>
                            <div className="flex gap-1 mt-1">
                              <Input
                                placeholder={unit.ad_slot_id_masked || "1234567890"}
                                value={unit.ad_slot_id || ""}
                                onChange={(e) => updateAdUnit(index, "ad_slot_id", e.target.value)}
                                className="bg-zinc-800 border-zinc-600 text-zinc-300 font-mono"
                              />
                              {unit.ad_slot_id_masked && (
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => copyToClipboard(unit.ad_slot_id_masked)}
                                  className="text-zinc-400"
                                >
                                  <Copy size={14} />
                                </Button>
                              )}
                            </div>
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-xs">Format</Label>
                            <Select 
                              value={unit.format || "auto"}
                              onValueChange={(v) => updateAdUnit(index, "format", v)}
                            >
                              <SelectTrigger className="bg-zinc-800 border-zinc-600 text-zinc-300 mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="auto">Auto</SelectItem>
                                <SelectItem value="horizontal">Horizontal</SelectItem>
                                <SelectItem value="rectangle">Rectangle</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* What Google Needs */}
            <Card className="bg-zinc-800 border-zinc-700">
              <CardHeader>
                <CardTitle className="text-white">What Google Needs From Us</CardTitle>
                <CardDescription className="text-zinc-400">Setup requirements for AdSense approval</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Domains */}
                <div>
                  <Label className="text-zinc-300">Production Domains</Label>
                  <Textarea
                    placeholder="Enter each domain on a new line, e.g.:
example.com
www.example.com"
                    value={(adsenseSettings.domains || []).join("\n")}
                    onChange={(e) => setAdsenseSettings({
                      ...adsenseSettings, 
                      domains: e.target.value.split("\n").filter(d => d.trim())
                    })}
                    className="bg-zinc-900 border-zinc-600 text-zinc-300 mt-1 min-h-[80px] font-mono"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Domains where ads will run (must match AdSense site list)</p>
                </div>

                {/* ads.txt */}
                <div className="p-4 bg-zinc-900 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-white font-medium">ads.txt File</Label>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => copyToClipboard(`${window.location.origin}/api/ads.txt`)}
                      className="text-zinc-400"
                    >
                      <Copy size={14} className="mr-2" />
                      Copy URL
                    </Button>
                  </div>
                  <p className="text-sm text-zinc-400">
                    Your ads.txt is served at: <code className="bg-zinc-800 px-2 py-0.5 rounded">{window.location.origin}/api/ads.txt</code>
                  </p>
                  <div>
                    <Label className="text-zinc-400 text-xs">ads.txt Content</Label>
                    <Textarea
                      placeholder="google.com, pub-XXXXXXXXXXXXXXXX, DIRECT, f08c47fec0942fa0"
                      value={adsenseSettings.ads_txt_content || ""}
                      onChange={(e) => setAdsenseSettings({...adsenseSettings, ads_txt_content: e.target.value})}
                      className="bg-zinc-800 border-zinc-600 text-zinc-300 mt-1 min-h-[60px] font-mono text-sm"
                    />
                    <p className="text-xs text-zinc-500 mt-1">
                      Paste your ads.txt line from AdSense here. If left blank and Publisher ID is set, a standard line will be auto-generated.
                    </p>
                  </div>
                </div>

                {/* Status Indicators */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-zinc-900 rounded-lg flex items-center gap-3">
                    {adsenseSettings.publisher_id_configured ? (
                      <CheckCircle size={20} className="text-emerald-400" />
                    ) : (
                      <Warning size={20} className="text-amber-400" />
                    )}
                    <div>
                      <p className="text-sm text-white">Publisher ID</p>
                      <p className="text-xs text-zinc-400">
                        {adsenseSettings.publisher_id_configured ? "Configured" : "Not configured"}
                      </p>
                    </div>
                  </div>
                  <div className="p-3 bg-zinc-900 rounded-lg flex items-center gap-3">
                    {adsenseSettings.ads_txt_content ? (
                      <CheckCircle size={20} className="text-emerald-400" />
                    ) : (
                      <Warning size={20} className="text-amber-400" />
                    )}
                    <div>
                      <p className="text-sm text-white">ads.txt</p>
                      <p className="text-xs text-zinc-400">
                        {adsenseSettings.ads_txt_content ? "Configured" : "Using auto-generated"}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ad Impression Logs */}
            <Card className="bg-zinc-800 border-zinc-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Ad Impression Logs</CardTitle>
                    <CardDescription className="text-zinc-400">Recent ad render events and suppression reasons</CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={fetchAdsenseLogs} className="text-zinc-400 hover:text-white">
                    <ArrowClockwise size={16} className="mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-3 bg-zinc-900 rounded-lg text-center">
                    <p className="text-2xl font-bold text-white">{adsenseStats.total || 0}</p>
                    <p className="text-xs text-zinc-400">Total Events</p>
                  </div>
                  <div className="p-3 bg-zinc-900 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-400">{adsenseStats.rendered || 0}</p>
                    <p className="text-xs text-zinc-400">Rendered</p>
                  </div>
                  <div className="p-3 bg-zinc-900 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-400">{adsenseStats.suppressed || 0}</p>
                    <p className="text-xs text-zinc-400">Suppressed</p>
                  </div>
                </div>

                {/* Suppression Reasons */}
                {adsenseStats.suppression_reasons && Object.keys(adsenseStats.suppression_reasons).length > 0 && (
                  <div className="mb-6 p-3 bg-zinc-900 rounded-lg">
                    <Label className="text-zinc-400 text-xs">Suppression Reasons</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {Object.entries(adsenseStats.suppression_reasons).map(([reason, count]) => (
                        <Badge key={reason} className="bg-zinc-700 text-zinc-300">
                          {reason.replace(/_/g, " ")}: {count}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Logs */}
                {adsenseLogs.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No ad impression logs yet</p>
                ) : (
                  <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-zinc-800">
                        <tr className="border-b border-zinc-700">
                          <th className="text-left py-2 px-3 text-zinc-400 font-medium text-xs">Placement</th>
                          <th className="text-left py-2 px-3 text-zinc-400 font-medium text-xs">Tenant</th>
                          <th className="text-left py-2 px-3 text-zinc-400 font-medium text-xs">Status</th>
                          <th className="text-left py-2 px-3 text-zinc-400 font-medium text-xs">Reason</th>
                          <th className="text-left py-2 px-3 text-zinc-400 font-medium text-xs">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adsenseLogs.map((log, i) => (
                          <tr key={log.log_id || i} className="border-b border-zinc-700/50">
                            <td className="py-2 px-3 text-zinc-300 text-sm">{log.placement_key?.replace(/_/g, " ")}</td>
                            <td className="py-2 px-3 text-zinc-400 font-mono text-xs">{log.tenant_id?.slice(-8) || "-"}</td>
                            <td className="py-2 px-3">
                              <Badge className={log.rendered ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"}>
                                {log.rendered ? "Rendered" : "Suppressed"}
                              </Badge>
                            </td>
                            <td className="py-2 px-3 text-zinc-400 text-xs">{log.suppression_reason?.replace(/_/g, " ") || "-"}</td>
                            <td className="py-2 px-3 text-zinc-500 text-xs">{formatDate(log.timestamp)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Service Types Tab */}
        <TabsContent value="service-types">
          <ServiceTypesPanel />
        </TabsContent>

        {/* Jobs Tab - Admin Job Runner */}
        {ENABLE_ADMIN_JOB_RUNNER && (
          <TabsContent value="jobs">
            <div className="space-y-6">
              <Card className="bg-zinc-800 border-zinc-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Lightning size={20} />
                    Admin Job Runner
                  </CardTitle>
                  <CardDescription className="text-zinc-400">
                    Manually trigger scheduled jobs for testing and operations
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Appointment Reminders */}
                  <div className="p-4 bg-zinc-900 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-white font-medium">Appointment Reminders</h4>
                        <p className="text-zinc-400 text-sm">Send reminders for upcoming visits to customers</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => runAdminJob("appointment_reminders", true)}
                          disabled={jobRunning !== null}
                          className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                          data-testid="job-appointment-reminders-dry-run"
                        >
                          {jobRunning === "appointment_reminders_dry" ? (
                            <><ArrowClockwise size={14} className="mr-1 animate-spin" />Running...</>
                          ) : (
                            <>Dry Run</>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => runAdminJob("appointment_reminders", false)}
                          disabled={jobRunning !== null}
                          className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                          data-testid="job-appointment-reminders-run"
                        >
                          {jobRunning === "appointment_reminders_live" ? (
                            <><ArrowClockwise size={14} className="mr-1 animate-spin" />Running...</>
                          ) : (
                            <><Play size={14} className="mr-1" />Run</>
                          )}
                        </Button>
                      </div>
                    </div>
                    {jobResults.appointment_reminders && (
                      <div className="mt-3 p-3 bg-zinc-950 rounded border border-zinc-700">
                        <pre className="text-xs text-zinc-300 whitespace-pre-wrap overflow-x-auto">
                          {JSON.stringify(jobResults.appointment_reminders, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>

                  {/* Invoice Followups */}
                  <div className="p-4 bg-zinc-900 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-white font-medium">Invoice Followups</h4>
                        <p className="text-zinc-400 text-sm">Schedule and send overdue invoice reminders</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => runAdminJob("invoice_followups", true)}
                          disabled={jobRunning !== null}
                          className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                          data-testid="job-invoice-followups-dry-run"
                        >
                          {jobRunning === "invoice_followups_dry" ? (
                            <><ArrowClockwise size={14} className="mr-1 animate-spin" />Running...</>
                          ) : (
                            <>Dry Run</>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => runAdminJob("invoice_followups", false)}
                          disabled={jobRunning !== null}
                          className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                          data-testid="job-invoice-followups-run"
                        >
                          {jobRunning === "invoice_followups_live" ? (
                            <><ArrowClockwise size={14} className="mr-1 animate-spin" />Running...</>
                          ) : (
                            <><Play size={14} className="mr-1" />Run</>
                          )}
                        </Button>
                      </div>
                    </div>
                    {jobResults.invoice_followups && (
                      <div className="mt-3 p-3 bg-zinc-950 rounded border border-zinc-700">
                        <pre className="text-xs text-zinc-300 whitespace-pre-wrap overflow-x-auto">
                          {JSON.stringify(jobResults.invoice_followups, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>

                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-sm text-blue-300">
                      <strong>Dry Run</strong> counts eligible items without sending. <strong>Run</strong> executes the job and sends real notifications.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}

        {/* Custom Ads Tab */}
        {ENABLE_CUSTOM_ADS_ADMIN && (
          <TabsContent value="custom_ads">
            <CustomAdsPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
